import { Component, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import type { ErrorInfo, ReactNode } from "react";
import {
  Activity,
  ArrowLeft,
  ArrowRight,
  Bell,
  CalendarDays,
  CalendarRange,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleAlert,
  CircleDollarSign,
  Clock3,
  Filter,
  LayoutDashboard,
  LockKeyhole,
  ListTodo,
  LogOut,
  Menu,
  MessageSquareText,
  MoreHorizontal,
  NotebookPen,
  PackagePlus,
  Plus,
  Search,
  Settings,
  Target,
  Trash2,
  TrendingUp,
  UserRound,
  UsersRound,
  WalletCards,
  X,
  XCircle
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { Appointment, AppointmentStatus, CancellationMode, Customer, LedgerEntry, PackageRecord } from "./types";
import {
  CustomerOverlay,
  FullCustomersPage,
  FullFinancePage,
  FullMessagesPage,
  FullOpportunitiesPage,
  PackageCreationForm,
  TeamPage
} from "./FullModules";
import { GoalsTasksPage, NotesPage } from "./Workspaces";
import { SystemHealthBoundary, SystemHealthPage } from "./SystemHealthPage";
import { WorkshopPage } from "./WorkshopPage";
import { WeeklyScheduleModal } from "./WeeklyScheduleModal";
import { useStudio } from "./StudioContext";
import type { OperationState } from "./StudioContext";
import { studioErrorCopy } from "./lib/api";
import {
  findAppointmentPreferenceConflicts,
  type AppointmentPreferenceConflict
} from "./lib/appointmentPreferences";
import { addIsoDays, isoDateAtNoon, istanbulHour, istanbulToday, shiftIsoMonth } from "./lib/date";
import { packageIdentity } from "./lib/packageIdentity";
import logoUrl from "./logo/logo2.png";

const logoSrc =
  typeof logoUrl === "string" ? logoUrl : (logoUrl as unknown as { src: string }).src;

const SEEN_NOTIFICATION_IDS_KEY = "thh.portal.seen-notification-ids";
const SEEN_MOBILE_ACTIVITY_IDS_KEY = "thh.portal.seen-mobile-activity-ids";

function readSeenAlertIds(storageKey: string) {
  if (typeof window === "undefined") return [] as string[];
  try {
    const value = JSON.parse(window.localStorage.getItem(storageKey) ?? "[]");
    return Array.isArray(value)
      ? value.filter((item): item is string => typeof item === "string").slice(-400)
      : [];
  } catch {
    return [];
  }
}

function mergeSeenAlertIds(current: string[], ids: string[], storageKey: string) {
  const next = Array.from(new Set([...current, ...ids])).slice(-400);
  try {
    window.localStorage.setItem(storageKey, JSON.stringify(next));
  } catch {
    // Sayaç bu oturumda yine temizlenir; storage erişimi zorunlu değildir.
  }
  return next;
}

type Page =
  | "dashboard"
  | "appointments"
  | "workshops"
  | "customers"
  | "finance"
  | "opportunities"
  | "messages"
  | "team"
  | "notes"
  | "work"
  | "system";

const navItems: Array<{ id: Page; label: string; icon: LucideIcon }> = [
  { id: "dashboard", label: "Günlük akış", icon: LayoutDashboard },
  { id: "customers", label: "Müşteriler", icon: UsersRound },
  { id: "appointments", label: "Randevular", icon: CalendarDays },
  { id: "workshops", label: "Atölyeler", icon: CalendarRange },
  { id: "finance", label: "Kasa & paketler", icon: WalletCards },
  { id: "opportunities", label: "Fırsatlar", icon: Target },
  { id: "messages", label: "Mesajlar", icon: MessageSquareText },
  { id: "team", label: "Ekip programı", icon: UserRound },
  { id: "notes", label: "Notlar", icon: NotebookPen },
  { id: "work", label: "Hedefler & görevler", icon: ListTodo },
  { id: "system", label: "Sistem kontrolü", icon: Activity }
];

class PortalPageBoundary extends Component<
  { children: ReactNode; label: string },
  { failed: boolean; retryKey: number }
> {
  state = { failed: false, retryKey: 0 };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // Bir modüldeki veri uyumsuzluğu portalın tamamını boş ekrana düşürmemeli.
  }

  private retry = () => {
    this.setState((current) => ({
      failed: false,
      retryKey: current.retryKey + 1
    }));
  };

  render() {
    if (this.state.failed) {
      return (
        <section className="system-page-fallback" role="alert">
          <XCircle size={30} />
          <span className="eyebrow">The Heal Hub · Portal</span>
          <h1>{this.props.label} ekranı hazırlanamadı</h1>
          <p>Diğer bölümler çalışmaya devam ediyor. Güncel verilerle yeniden açmak için tekrar deneyin.</p>
          <button className="button primary" onClick={this.retry}>
            Yeniden dene
          </button>
        </section>
      );
    }

    return <div key={this.state.retryKey}>{this.props.children}</div>;
  }
}

const statusLabel: Record<AppointmentStatus, string> = {
  planned: "Planlandı",
  completed: "Tamamlandı",
  cancelled: "İptal",
  blocked: "Bloke"
};

function useEscapeClose(onClose: () => void, enabled = true) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!enabled) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeRef.current();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [enabled]);
}

function useTrainerNames() {
  const { staff } = useStudio();
  return useMemo(
    () => staff.filter((member) => member.isTrainer).map((member) => member.name),
    [staff]
  );
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0
  }).format(amount);

const fromIsoDate = isoDateAtNoon;
const shiftDate = addIsoDays;
const longDate = (value: string) =>
  new Intl.DateTimeFormat("tr-TR", { day: "numeric", month: "long", year: "numeric", weekday: "long" })
    .format(fromIsoDate(value));
const compactDate = (value: string) =>
  new Intl.DateTimeFormat("tr-TR", { day: "numeric", month: "short", year: "numeric" })
    .format(fromIsoDate(value));

interface BookablePackage {
  id: string;
  detail: string;
  remaining: number;
  planned: number;
  available: number;
  expiryDate?: string;
  expired: boolean;
  cancellationRemaining: number;
}

function getBookablePackages(
  customer: Customer | undefined,
  records: PackageRecord[],
  ledger: LedgerEntry[],
  appointments: Appointment[]
): BookablePackage[] {
  if (!customer) return [];
  const customerRecords = records.filter(
    (record) =>
      record.customerId === customer.id &&
      record.isOperational !== false &&
      record.statusCode !== "CLOSED" &&
      record.statusCode !== "CANCELLED"
  );
  const packageRows = customerRecords.length
    ? customerRecords.map((record) => {
        const recordReference = `R${record.id.replace(/\D/g, "")}`;
        const recordLedger = ledger.find(
          (entry) =>
            entry.description === "Record" &&
            entry.customerId === customer.id &&
            entry.reference === recordReference
        );
        const remaining =
          record.remainingSessions ??
          recordLedger?.remainingQuantity ??
          record.quantity;
        const expiryDate = record.expiryDate;
        const expired =
          record.isExpired ??
          Boolean(expiryDate && expiryDate < istanbulToday());
        const planned =
          record.plannedSessions ??
          appointments.filter(
            (appointment) =>
              appointment.customerId === customer.id &&
              appointment.status === "planned" &&
              (appointment.recordId
                ? appointment.recordId === record.id
                : appointment.record === record.detail)
          ).length;
        return {
          id: record.id,
          detail: packageIdentity(record, records).label,
          remaining,
          planned,
          available:
            record.availableToBook ??
            (expired ? 0 : Math.max(0, remaining - planned)),
          expiryDate,
          expired,
          cancellationRemaining: Math.max(
            0,
            (record.cancellationAllowance ?? 0) - (record.cancellationsUsed ?? 0)
          )
        };
      })
    : [{
        id: `fallback-${customer.id}`,
        detail: customer.packageName,
        remaining: customer.remainingSessions,
        planned: appointments.filter(
          (appointment) =>
            appointment.customerId === customer.id &&
            appointment.status === "planned"
        ).length,
        available: 0,
        expiryDate: undefined,
        expired: false,
        cancellationRemaining: 0
      }];

  return packageRows
    .map((item) => ({
      ...item,
      available: item.expired ? 0 : Math.max(0, item.remaining - item.planned)
    }))
    .filter((item) => item.remaining > 0);
}

function packageValidForSchedule(item: BookablePackage | undefined, date: string, repeatCount: number) {
  if (!item || !date) return false;
  if (!item.expiryDate) return true;
  return shiftDate(date, (repeatCount - 1) * 7) <= item.expiryDate;
}

function findAppointmentConflicts(
  appointments: Appointment[],
  date: string,
  time: string,
  excludeAppointmentId?: string
) {
  if (!date || !time) return [];
  const [hour, minute] = time.split(":").map(Number);
  const selectedStart = hour * 60 + minute;
  const selectedEnd = selectedStart + 60;
  return appointments.filter((appointment) => {
    if (
      appointment.id === excludeAppointmentId ||
      appointment.date !== date ||
      appointment.status === "cancelled"
    ) return false;
    const [startHour, startMinute] = appointment.time.split(":").map(Number);
    const [endHour, endMinute] = appointment.endTime.split(":").map(Number);
    const appointmentStart = startHour * 60 + startMinute;
    const appointmentEnd = endHour * 60 + endMinute;
    return selectedStart < appointmentEnd && appointmentStart < selectedEnd;
  });
}

function appointmentPreferenceMessage(conflict: AppointmentPreferenceConflict) {
  if (
    conflict.code === "SELECTED_REQUIRES_EXCLUSIVE" ||
    conflict.code === "EXISTING_REQUIRES_EXCLUSIVE"
  ) {
    return `${conflict.customerName} aynı saatte başka danışan istemiyor.`;
  }
  return `${conflict.customerName} aynı saatte erkek danışan istemiyor.`;
}

export function TimeConflictList({
  conflicts,
  preferenceConflicts = []
}: {
  conflicts: Appointment[];
  preferenceConflicts?: AppointmentPreferenceConflict[];
}) {
  if (preferenceConflicts.length) {
    return (
      <div className="time-conflicts blocking" role="alert">
        <div className="time-conflict-head"><CircleAlert size={17} /><span><strong>Bu saate randevu eklenemez.</strong> Danışanların seans eşleşme tercihleriyle çakışıyor.</span></div>
        {preferenceConflicts.map((conflict) => (
          <div className="time-conflict-rule" key={`${conflict.date}:${conflict.appointmentId}:${conflict.code}`}>
            <XCircle size={16} />
            <span><strong>{appointmentPreferenceMessage(conflict)}</strong><small>{compactDate(conflict.date)} tarihindeki seans</small></span>
          </div>
        ))}
      </div>
    );
  }
  if (!conflicts.length) {
    return <div className="time-available"><CheckCircle2 size={17} /><span>Seçilen saat içerideki program açısından boş görünüyor.</span></div>;
  }
  return (
    <div className="time-conflicts">
      <div className="time-conflict-head"><Clock3 size={17} /><span><strong>Bu saatte içeride {conflicts.length} danışan var.</strong> Bu yalnızca bilgilendirmedir; aynı saate birden fazla danışan ekleyebilirsiniz.</span></div>
      {conflicts.map((conflict) => (
        <div className="time-conflict-row" key={conflict.id}>
          <span className="avatar tiny">{conflict.initials}</span>
          <span><strong>{conflict.customer}</strong><small>{conflict.type} · {conflict.trainer}</small></span>
          <span><strong>{conflict.time}–{conflict.endTime}</strong><small>{statusLabel[conflict.status]}</small></span>
        </div>
      ))}
    </div>
  );
}

function Sidebar({
  page,
  onNavigate,
  open,
  onClose,
  onSettings,
  onProfile
}: {
  page: Page;
  onNavigate: (page: Page) => void;
  open: boolean;
  onClose: () => void;
  onSettings: () => void;
  onProfile: () => void;
}) {
  const { appointments } = useStudio();
  const todayAppointments = appointments.filter(
    (appointment) => appointment.date === istanbulToday()
  );
  const waitingToday = todayAppointments.filter(
    (appointment) => appointment.status === "planned"
  ).length;
  return (
    <>
      <button
        className={`sidebar-scrim ${open ? "is-open" : ""}`}
        onClick={onClose}
        aria-label="Menüyü kapat"
      />
      <aside className={`sidebar ${open ? "is-open" : ""}`}>
        <div className="brand">
          <span className="brand-logo"><img src={logoSrc} alt="" /></span>
          <div>
            <strong>The Heal Hub</strong>
            <span>Portal</span>
          </div>
          <button className="icon-button sidebar-close" onClick={onClose} aria-label="Kapat">
            <X size={19} />
          </button>
        </div>

        <nav className="primary-nav" aria-label="Ana menü">
          <p className="nav-label">Operasyon</p>
          {navItems.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={page === id ? "active" : ""}
              onClick={() => {
                onNavigate(id);
                onClose();
              }}
            >
              <Icon size={19} strokeWidth={1.8} />
              <span>{label}</span>
              {id === "appointments" && waitingToday > 0 && <em>{waitingToday}</em>}
            </button>
          ))}
        </nav>

        <div className="sidebar-foot">
          <div className="mini-card">
            <CalendarDays size={18} />
            <div>
              <strong>Bugünün programı</strong>
              <span>{todayAppointments.length} randevu · {waitingToday} bekliyor</span>
            </div>
          </div>
          <button className="settings-row" onClick={onSettings}>
            <Settings size={18} />
            <span>Ayarlar</span>
          </button>
          <button className="profile" onClick={onProfile}>
            <span className="avatar">THH</span>
            <div>
              <strong>THH Portal</strong>
              <span>Paylaşımlı cihaz</span>
            </div>
            <MoreHorizontal size={18} />
          </button>
        </div>
      </aside>
    </>
  );
}

function Topbar({
  onMenu,
  onNewAppointment,
  query,
  onQueryChange,
  onOpenCustomer,
  onOpenAppointment,
  onNavigate
}: {
  onMenu: () => void;
  onNewAppointment: () => void;
  query: string;
  onQueryChange: (value: string) => void;
  onOpenCustomer: (customerId: string) => void;
  onOpenAppointment: (appointmentId: string) => void;
  onNavigate: (page: Page) => void;
}) {
  const {
    customers,
    appointments,
    mobileActivities,
    teamNotes,
    workTasks,
    workspaceSettings,
    connectionState,
    lastSyncedAt
  } = useStudio();
  const searchInput = useRef<HTMLInputElement>(null);
  const notificationRef = useRef<HTMLDivElement>(null);
  const mobileActivityRef = useRef<HTMLDivElement>(null);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [mobileActivityOpen, setMobileActivityOpen] = useState(false);
  const [expandedMobileActivityId, setExpandedMobileActivityId] = useState<string | null>(null);
  const [seenNotificationIds, setSeenNotificationIds] = useState<string[]>(
    () => readSeenAlertIds(SEEN_NOTIFICATION_IDS_KEY)
  );
  const [seenMobileActivityIds, setSeenMobileActivityIds] = useState<string[]>(
    () => readSeenAlertIds(SEEN_MOBILE_ACTIVITY_IDS_KEY)
  );
  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLocaleLowerCase() === "k") {
        event.preventDefault();
        searchInput.current?.focus();
      }
    };
    window.addEventListener("keydown", focusSearch);
    return () => window.removeEventListener("keydown", focusSearch);
  }, []);
  useEffect(() => {
    const closeNotifications = (event: PointerEvent) => {
      if (!notificationRef.current?.contains(event.target as Node)) setNotificationsOpen(false);
      if (!mobileActivityRef.current?.contains(event.target as Node)) {
        setMobileActivityOpen(false);
        setExpandedMobileActivityId(null);
      }
    };
    window.addEventListener("pointerdown", closeNotifications);
    return () => window.removeEventListener("pointerdown", closeNotifications);
  }, []);
  const normalizedQuery = query.trim().toLocaleLowerCase("tr-TR");
  const customerResults = normalizedQuery
    ? customers.filter((customer) =>
        `${customer.name} ${customer.id} ${customer.phone}`.toLocaleLowerCase("tr-TR").includes(normalizedQuery)
      ).slice(0, 5)
    : [];
  const appointmentResults = normalizedQuery
    ? appointments.filter((appointment) =>
        `${appointment.customer} ${appointment.customerId} ${appointment.id} ${appointment.type}`.toLocaleLowerCase("tr-TR").includes(normalizedQuery)
      ).slice(0, 4)
    : [];
  const todayDate = istanbulToday();
  const tomorrowDate = addIsoDays(todayDate, 1);
  const dueNotes = teamNotes.filter(
    (note) => note.status === "OPEN" && note.dueOn && note.dueOn <= todayDate
  );
  const dueTasks = workTasks.filter(
    (task) => task.status !== "DONE" && task.dueOn <= todayDate
  );
  const tomorrowAppointments = workspaceSettings.sessionReminders
    ? appointments.filter(
        (appointment) =>
          appointment.date === tomorrowDate &&
          appointment.status === "planned" &&
          appointment.entryType !== "hold"
      )
    : [];
  const notificationCount = dueNotes.length + dueTasks.length + (tomorrowAppointments.length ? 1 : 0);
  const tomorrowNotificationId = tomorrowAppointments.length
    ? `tomorrow-${tomorrowDate}-${tomorrowAppointments.map((appointment) => appointment.id).sort().join(",")}`
    : null;
  const notificationAlertIds = [
    ...dueNotes.map((note) => `note-${note.id}-${note.dueOn}`),
    ...dueTasks.map((task) => `task-${task.id}-${task.dueOn}`),
    ...(tomorrowNotificationId ? [tomorrowNotificationId] : [])
  ];
  const seenNotificationIdSet = new Set(seenNotificationIds);
  const seenMobileActivityIdSet = new Set(seenMobileActivityIds);
  const unreadNotificationCount = notificationAlertIds.filter(
    (id) => !seenNotificationIdSet.has(id)
  ).length;
  const unreadMobileActivityCount = mobileActivities.filter(
    (activity) => !seenMobileActivityIdSet.has(`${activity.eventType}-${activity.appointmentId}`)
  ).length;
  const markNotificationsRead = () => {
    setSeenNotificationIds((current) =>
      mergeSeenAlertIds(current, notificationAlertIds, SEEN_NOTIFICATION_IDS_KEY)
    );
  };
  const markMobileActivitiesRead = () => {
    setSeenMobileActivityIds((current) =>
      mergeSeenAlertIds(
        current,
        mobileActivities.map((activity) => `${activity.eventType}-${activity.appointmentId}`),
        SEEN_MOBILE_ACTIVITY_IDS_KEY
      )
    );
  };
  const formatMobileTime = (value: string, includeDate = true) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "Tarih bilgisi alınamadı";
    return new Intl.DateTimeFormat("tr-TR", {
      timeZone: "Europe/Istanbul",
      ...(includeDate ? { day: "2-digit", month: "short" } : {}),
      hour: "2-digit",
      minute: "2-digit"
    }).format(date);
  };
  const openNotificationPage = (page: Page) => {
    onNavigate(page);
    setNotificationsOpen(false);
  };

  return (
    <header className="topbar">
      <button className="icon-button menu-button" onClick={onMenu} aria-label="Menüyü aç">
        <Menu size={21} />
      </button>
      <div className="global-search">
        <Search size={18} />
        <input
          aria-label="Genel arama"
          placeholder="Müşteri, randevu veya telefon ara…"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          ref={searchInput}
        />
        <kbd>Ctrl K</kbd>
        {normalizedQuery && (
          <div className="global-search-results">
            {!customerResults.length && !appointmentResults.length && (
              <div className="search-empty">Bu aramayla eşleşen kayıt bulunamadı.</div>
            )}
            {!!customerResults.length && <span className="search-result-label">Müşteriler</span>}
            {customerResults.map((customer) => (
              <button key={customer.id} onClick={() => onOpenCustomer(customer.id)}>
                <span className="avatar tiny">{customer.initials}</span>
                <span><strong>{customer.name}</strong><small>{customer.phone} · {customer.remainingSessions} seans</small></span>
                <ChevronRight size={15} />
              </button>
            ))}
            {!!appointmentResults.length && <span className="search-result-label">Randevular</span>}
            {appointmentResults.map((appointment) => (
              <button key={appointment.id} onClick={() => onOpenAppointment(appointment.id)}>
                <CalendarDays size={17} />
                <span><strong>{appointment.customer}</strong><small>{appointment.date} · {appointment.time} · {appointment.type}</small></span>
                <ChevronRight size={15} />
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="topbar-actions">
        <span className={`sync-state ${connectionState === "error" ? "stale" : ""}`} title={lastSyncedAt ? `Son güncelleme: ${new Date(lastSyncedAt).toLocaleString("tr-TR")}` : undefined}><i /> {connectionState === "error" ? "Son veriler gösteriliyor" : "Sistem güncel"}</span>
        <div className="notification-center" ref={notificationRef}>
          <button className={`icon-button notification-button ${notificationsOpen ? "active" : ""}`} aria-label={`Bildirimler${unreadNotificationCount ? `, ${unreadNotificationCount} okunmamış uyarı` : ""}`} onClick={() => {
            setMobileActivityOpen(false);
            if (!notificationsOpen) markNotificationsRead();
            setNotificationsOpen(!notificationsOpen);
          }}>
            <Bell size={19} />
            {unreadNotificationCount > 0 && <i>{unreadNotificationCount > 9 ? "9+" : unreadNotificationCount}</i>}
          </button>
          {notificationsOpen && (
            <aside className="notification-popover" aria-label="Bildirim merkezi">
              <div className="notification-popover-head">
                <div><span className="eyebrow">THH Portal</span><h3>Bugün hatırlanacaklar</h3></div>
                <span>{notificationCount}</span>
              </div>
              <div className="notification-list">
                {dueNotes.map((note) => <button key={`note-${note.id}`} onClick={() => openNotificationPage("notes")}>
                  <span className={`notification-mark ${note.dueOn! < todayDate ? "overdue" : "today"}`}><NotebookPen size={16} /></span>
                  <span><strong>{note.body}</strong><small>{note.dueOn! < todayDate ? "Takip tarihi geçti" : "Bugün takip edilecek"}{note.assignedTo ? ` · ${note.assignedTo}` : ""}</small></span>
                  <ChevronRight size={15} />
                </button>)}
                {dueTasks.map((task) => <button key={`task-${task.id}`} onClick={() => openNotificationPage("work")}>
                  <span className={`notification-mark ${task.dueOn < todayDate ? "overdue" : "today"}`}><ListTodo size={16} /></span>
                  <span><strong>{task.title}</strong><small>{task.dueOn < todayDate ? "Son tarih geçti" : "Bugün tamamlanacak"} · {task.assignedTo}</small></span>
                  <ChevronRight size={15} />
                </button>)}
                {!!tomorrowAppointments.length && <button onClick={() => openNotificationPage("appointments")}>
                  <span className="notification-mark tomorrow"><CalendarDays size={16} /></span>
                  <span><strong>Yarın {tomorrowAppointments.length} planlı seans var</strong><small>Programı şimdiden gözden geçir</small></span>
                  <ChevronRight size={15} />
                </button>}
                {!notificationCount && <div className="notification-empty"><CheckCircle2 size={23} /><strong>Şimdilik her şey yolunda</strong><span>Son tarihi yaklaşan not veya görev yok.</span></div>}
              </div>
              <div className="notification-foot"><span>Not ve görev son tarihleri otomatik güncellenir.</span></div>
            </aside>
          )}
        </div>
        <div className="mobile-activity-center" ref={mobileActivityRef}>
          <button
            className={`icon-button mobile-activity-button ${unreadMobileActivityCount ? "has-events" : ""} ${mobileActivityOpen ? "active" : ""}`}
            aria-label={`Mobil uygulama hareketleri${unreadMobileActivityCount ? `, ${unreadMobileActivityCount} okunmamış değişiklik` : ""}`}
            aria-expanded={mobileActivityOpen}
            onClick={() => {
              setNotificationsOpen(false);
              if (!mobileActivityOpen) markMobileActivitiesRead();
              setMobileActivityOpen(!mobileActivityOpen);
              setExpandedMobileActivityId(null);
            }}
          >
            <CircleAlert size={20} />
            {unreadMobileActivityCount > 0 && <i>{unreadMobileActivityCount > 9 ? "9+" : unreadMobileActivityCount}</i>}
          </button>
          {mobileActivityOpen && (
            <aside className="mobile-activity-popover" aria-label="Mobil uygulama hareketleri">
              <div className="mobile-activity-head">
                <div>
                  <span className="eyebrow">Müşteri uygulaması</span>
                  <h3>Son 48 saatte değişenler</h3>
                </div>
                <span>{mobileActivities.length}</span>
              </div>
              <div className="mobile-activity-list">
                {mobileActivities.map((activity) => {
                  const expanded = expandedMobileActivityId === activity.id;
                  const appointmentIsLoaded = appointments.some((appointment) => appointment.id === activity.appointmentId);
                  return (
                    <div className={`mobile-activity-item ${activity.eventType} ${expanded ? "expanded" : ""}`} key={activity.id}>
                      <button
                        className="mobile-activity-summary"
                        onClick={() => setExpandedMobileActivityId(expanded ? null : activity.id)}
                        aria-expanded={expanded}
                      >
                        <span className="mobile-activity-mark">
                          {activity.eventType === "created" ? <Plus size={17} /> : <X size={17} />}
                        </span>
                        <span className="mobile-activity-copy">
                          <strong>{activity.customer}</strong>
                          <small>
                            {activity.eventType === "created" ? "Yeni randevu oluşturdu" : "Randevusunu iptal etti"}
                            {" · "}
                            {formatMobileTime(activity.occurredAt)}
                          </small>
                        </span>
                        <ChevronRight size={16} className="mobile-activity-chevron" />
                      </button>
                      {expanded && (
                        <div className="mobile-activity-detail">
                          <dl>
                            <div><dt>Randevu</dt><dd>{formatMobileTime(activity.scheduledAt)}</dd></div>
                            <div><dt>Seans</dt><dd>{activity.serviceType}</dd></div>
                            <div><dt>Sorumlu</dt><dd>{activity.trainer}</dd></div>
                          </dl>
                          <div className="mobile-activity-actions">
                            <button onClick={() => {
                              setMobileActivityOpen(false);
                              setExpandedMobileActivityId(null);
                              onOpenCustomer(activity.customerId);
                            }}>Müşteri kartı</button>
                            {appointmentIsLoaded && (
                              <button className="primary" onClick={() => {
                                setMobileActivityOpen(false);
                                setExpandedMobileActivityId(null);
                                onOpenAppointment(activity.appointmentId);
                              }}>Randevuyu aç</button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                {!mobileActivities.length && (
                  <div className="mobile-activity-empty">
                    <CheckCircle2 size={24} />
                    <strong>Yeni mobil hareket yok</strong>
                    <span>Son 48 saatte uygulamadan randevu oluşturulmadı veya iptal edilmedi.</span>
                  </div>
                )}
              </div>
              <div className="mobile-activity-foot">
                <span>07.00–00.00 arasında 15 dakikada bir yenilenir.</span>
                <i aria-hidden="true" />
              </div>
            </aside>
          )}
        </div>
        <button className="button primary compact" onClick={onNewAppointment}>
          <Plus size={18} />
          <span>Yeni seans</span>
        </button>
      </div>
    </header>
  );
}

function PageHeading({
  eyebrow,
  title,
  description,
  action
}: {
  eyebrow: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="page-heading">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        {description && <p>{description}</p>}
      </div>
      {action}
    </div>
  );
}

function DateRail({
  selectedDate,
  onChange,
  appointments
}: {
  selectedDate: string;
  onChange: (date: string) => void;
  appointments: Appointment[];
}) {
  const day = new Date(`${selectedDate}T12:00:00Z`).getUTCDay() || 7;
  const monday = addIsoDays(selectedDate, 1 - day);
  const dates = Array.from({ length: 7 }, (_, index) => {
    const iso = addIsoDays(monday, index);
    const date = fromIsoDate(iso);
    return {
      iso,
      day: new Intl.DateTimeFormat("tr-TR", { weekday: "short", timeZone: "Europe/Istanbul" }).format(date).replace(".", ""),
      date: String(Number(iso.slice(8, 10))),
      count: appointments.filter((appointment) => appointment.date === iso).length
    };
  });

  return (
    <div className="date-rail">
      <button className="date-arrow" aria-label="Önceki hafta" onClick={() => onChange(shiftDate(selectedDate, -7))}>
        <ArrowLeft size={17} />
      </button>
      <div className="date-list">
        {dates.map((item) => (
          <button key={item.iso} className={item.iso === selectedDate ? "active" : ""} onClick={() => onChange(item.iso)}>
            <span>{item.day}</span>
            <strong>{item.date}</strong>
            {item.count ? <i>{item.count} seans</i> : <i>—</i>}
          </button>
        ))}
      </div>
      <button className="date-arrow" aria-label="Sonraki hafta" onClick={() => onChange(shiftDate(selectedDate, 7))}>
        <ArrowRight size={17} />
      </button>
    </div>
  );
}

function AppointmentCard({
  appointment,
  onOpen
}: {
  appointment: Appointment;
  onOpen: (appointment: Appointment) => void;
}) {
  return (
    <article
      className={`appointment-card ${appointment.status}`}
      onClick={() => onOpen(appointment)}
    >
      <div className="appointment-time">
        <strong>{appointment.time}</strong>
        <span>{appointment.endTime}</span>
      </div>
      <div className="timeline-pin" />
      <div className="appointment-person">
        <span className="avatar soft">{appointment.initials}</span>
        <div>
          <strong>{appointment.customer}</strong>
          <span>{appointment.type}</span>
        </div>
      </div>
      <div className="appointment-meta">
        <span>
          <UserRound size={15} />
          {appointment.trainer}
        </span>
        {appointment.entryType === "hold" ? (
          <span className="hold-meta">Paket açmadan saat ayrıldı</span>
        ) : (
          <span className={appointment.remainingSessions <= 1 ? "danger-text" : ""}>
            {appointment.remainingSessions} seans kaldı
          </span>
        )}
      </div>
      <span className={`status ${appointment.status}`}>
        {appointment.status === "planned" && <Clock3 size={13} />}
        {appointment.status === "completed" && <Check size={13} />}
        {appointment.status === "cancelled" && <X size={13} />}
        {appointment.status === "blocked" && <LockKeyhole size={13} />}
        {statusLabel[appointment.status]}
      </span>
      <button className="icon-button subtle" aria-label="Randevuyu aç">
        <ChevronRight size={18} />
      </button>
    </article>
  );
}

function Dashboard({
  appointments,
  onOpenAppointment,
  onNewAppointment,
  onNewHold,
  onNavigate,
  onOpenCustomer
}: {
  appointments: Appointment[];
  onOpenAppointment: (appointment: Appointment) => void;
  onNewAppointment: () => void;
  onNewHold: () => void;
  onNavigate: (page: Page) => void;
  onOpenCustomer: (customerId: string) => void;
}) {
  const {
    customers,
    ledger,
    records,
    appointmentRange,
    appointmentsLoading,
    loadAppointmentRange,
    closeStudioDay,
    extendStudioClosurePackages
  } = useStudio();
  const [selectedDate, setSelectedDate] = useState(istanbulToday());
  const todayIso = istanbulToday();
  const [weeklyScheduleOpen, setWeeklyScheduleOpen] = useState(false);
  const [dayAction, setDayAction] = useState<"close" | "extend" | null>(null);
  const [closureReason, setClosureReason] = useState("");
  const [extensionDays, setExtensionDays] = useState(1);
  const [dayActionBusy, setDayActionBusy] = useState(false);
  const [dayActionError, setDayActionError] = useState("");
  const today = appointments
    .filter((appointment) => appointment.date === selectedDate)
    .sort((left, right) => left.time.localeCompare(right.time));
  const planned = today.filter((appointment) => appointment.status === "planned");
  const completed = today.filter((appointment) => appointment.status === "completed").length;
  const cancelled = today.filter((appointment) => appointment.status === "cancelled").length;
  const hour = istanbulHour();
  const greeting = hour < 12 ? "Günaydın" : hour < 18 ? "İyi günler" : "İyi akşamlar";
  const cancelledAppointment = today.find((appointment) => appointment.status === "cancelled");
  const todayCustomerIds = new Set(today.map((appointment) => appointment.customerId));
  const attendingCustomerIds = new Set(
    today.filter((appointment) => appointment.status !== "cancelled").map((appointment) => appointment.customerId)
  );
  const lowCustomers = customers.filter(
    (customer) =>
      attendingCustomerIds.has(customer.id) &&
      customer.remainingSessions > 0 &&
      customer.remainingSessions < 3
  );
  const pendingToday = Array.from(attendingCustomerIds).map((customerId) => {
    const entries = ledger.filter(
      (entry) =>
        entry.customerId === customerId &&
        entry.description === "Record" &&
        (entry.remainingAmount ?? 0) > 0
    );
    const customer = customers.find((item) => item.id === customerId);
    return {
      customerId,
      customer: customer?.name ?? entries[0]?.customer ?? customerId,
      amount: entries.reduce((sum, entry) => sum + (entry.remainingAmount ?? 0), 0),
      packages: entries.map((entry) => entry.relatedWith).join(", ")
    };
  }).filter((item) => item.amount > 0);
  const cancellationWarnings = records
    .filter((record) => todayCustomerIds.has(record.customerId))
    .map((record) => {
      const remaining = Math.max(
        0,
        (record.cancellationAllowance ?? 0) - (record.cancellationsUsed ?? 0)
      );
      const recordLedger = ledger.find(
        (entry) =>
          entry.description === "Record" &&
          entry.reference === `R${record.id.replace(/\D/g, "")}`
      );
      return {
        record,
        remaining,
        sessionRemaining:
          record.remainingSessions ??
          recordLedger?.remainingQuantity ??
          0
      };
    })
    .filter(({ record, remaining, sessionRemaining }) =>
      remaining === 1 &&
      sessionRemaining > 0 &&
      (!record.expiryDate || record.expiryDate >= selectedDate)
    );
  const attentionCount =
    lowCustomers.length +
    (cancelledAppointment ? 1 : 0) +
    pendingToday.length +
    cancellationWarnings.length;

  return (
    <>
      <section className="welcome-strip">
        <span className="welcome-logo"><img src={logoSrc} alt="The Heal Hub" /></span>
        <div>
          <span className="eyebrow">The Heal Hub - Portal</span>
          <h2>{greeting}, The Heal Hub ailesi.</h2>
          <p>Bugünün ritmini birlikte, sakin ve düzenli biçimde yönetelim.</p>
        </div>
      </section>
      <PageHeading
        eyebrow={longDate(selectedDate)}
        title="Günün akışı"
        description="Seansları takip et, günün planını düzenle ve müşteri işlemlerini aynı yerden tamamla."
        action={<div className="heading-actions dashboard-heading-actions">
          <button
            className="button dashboard-today-button"
            onClick={() => setSelectedDate(istanbulToday())}
            disabled={selectedDate === todayIso}
          >
            <CalendarDays size={17} />
            <span>Bugün</span>
          </button>
          <button className="button weekly-view-button" onClick={() => setWeeklyScheduleOpen(true)}>
            <span className="weekly-view-button-icon"><CalendarRange size={18} /></span>
            <span>Haftalık görünüm</span>
          </button>
          <button className="button ghost mobile-hide" onClick={() => onNavigate("appointments")}>
            Takvimin tamamı
            <ArrowRight size={17} />
          </button>
        </div>}
      />
      <DateRail selectedDate={selectedDate} onChange={setSelectedDate} appointments={appointments} />

      <section className="dashboard-grid">
        <div className="schedule-panel">
          <div className="section-title">
            <div>
              <span className="live-label"><i /> Canlı program</span>
              <h2>Randevu akışı</h2>
            </div>
            <div className="heading-actions dashboard-schedule-actions">
              <button className="button hold-button compact" onClick={onNewHold}>
                <Clock3 size={17} />
                Bloke et
              </button>
              <button className="button secondary compact" onClick={onNewAppointment}>
                <Plus size={17} />
                Seans ekle
              </button>
            </div>
          </div>
          <div className="studio-day-actions">
            <span><CalendarDays size={15} /> {compactDate(selectedDate)} · Stüdyo işlemleri</span>
            <i aria-hidden="true">—</i>
            <button className="text-button danger-text" onClick={() => { setDayActionError(""); setDayAction("close"); }}>
              Tüm randevuları iptal et
            </button>
            <i aria-hidden="true">·</i>
            <button className="text-button" onClick={() => { setDayActionError(""); setDayAction("extend"); }}>
              Paket sürelerini uzat
            </button>
          </div>
          <div className="timeline">
            {today.map((appointment) => (
              <AppointmentCard
                key={appointment.id}
                appointment={appointment}
                onOpen={onOpenAppointment}
              />
            ))}
            {!today.length && <div className="empty-state compact-empty">Bu gün için planlanmış seans yok.</div>}
          </div>
        </div>

        <aside className="dashboard-aside">
          <div className="summary-card dark">
            <div className="summary-head">
              <span>Günün temposu</span>
              <Activity size={19} />
            </div>
            <strong className="summary-fraction">{completed}<small>/{today.length}</small></strong>
            <span className="summary-caption">seans tamamlandı</span>
            <div className="progress"><span style={{ width: `${today.length ? (completed / today.length) * 100 : 0}%` }} /></div>
            <div className="summary-split">
              <div><strong>{planned.length}</strong><span>bekliyor</span></div>
              <div><strong>{cancelled}</strong><span>iptal</span></div>
            </div>
          </div>

          <div className="panel attention-panel">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">Takip gerekenler</span>
                <h3>Bugün gözden geçir</h3>
              </div>
              <span className="count-badge">{attentionCount}</span>
            </div>
            {lowCustomers.map((customer) => <button className="attention-row" key={`low-session-${customer.id}`} onClick={() => onOpenCustomer(customer.id)}>
              <span className="attention-icon warning"><WalletCards size={17} /></span>
              <span><strong>{customer.name}</strong><small>Son {customer.remainingSessions} seansı kaldı</small></span>
              <ChevronRight size={17} />
            </button>)}
            {cancelledAppointment && <button className="attention-row" onClick={() => onOpenAppointment(cancelledAppointment)}>
              <span className="attention-icon coral"><XCircle size={17} /></span>
              <span><strong>{cancelledAppointment.customer}</strong><small>{cancelledAppointment.date} seansı iptal</small></span>
              <ChevronRight size={17} />
            </button>}
            {cancellationWarnings.map(({ record }) => (
              <button className="attention-row" key={`cancel-right-${record.id}`} onClick={() => onOpenCustomer(record.customerId)}>
                <span className="attention-icon violet"><XCircle size={17} /></span>
                <span><strong>{record.customer} · 1 iptal hakkı kaldı</strong><small>{packageIdentity(record, records).label}{record.expiryDate ? ` · Son ${compactDate(record.expiryDate)}` : ""}</small></span>
                <ChevronRight size={17} />
              </button>
            ))}
            {pendingToday.map((pending) => (
              <button className="attention-row" key={`payment-${pending.customerId}`} onClick={() => onOpenCustomer(pending.customerId)}>
                <span className="attention-icon cash"><CircleDollarSign size={17} /></span>
                <span><strong>{pending.customer} · {formatCurrency(pending.amount)}</strong><small>{pending.packages} için bekleyen ödeme</small></span>
                <ChevronRight size={17} />
              </button>
            ))}
            {!attentionCount && <div className="attention-empty">Bu gün için takip gerektiren işlem yok.</div>}
          </div>

        </aside>
      </section>
      {dayAction && (
        <div className="confirm-popover studio-day-popover" role="alertdialog" aria-modal="true">
          <div className="confirm-icon"><CalendarDays size={24} /></div>
          <h3>{dayAction === "close" ? "Stüdyo bu gün kapalı mı?" : "Paket süreleri uzatılsın mı?"}</h3>
          <p>{longDate(selectedDate)}</p>
          {dayAction === "close" ? (
            <>
              <div className="confirm-impact protected">
                {planned.length} planlı randevu ve varsa bekleyen blokeler iptal edilir. Seans hakkı, iptal hakkı ve paket süresi değişmez.
              </div>
              <label className="field full cancellation-reason-field">
                <span>Kapanış açıklaması <i>isteğe bağlı</i></span>
                <textarea rows={2} maxLength={500} value={closureReason} onChange={(event) => setClosureReason(event.target.value)} placeholder="Örn. Resmî tatil" />
              </label>
            </>
          ) : (
            <>
              <label className="field full">
                <span>Kaç gün uzatılsın?</span>
                <div className="suffix-input"><input type="number" min="1" max="365" value={extensionDays} onChange={(event) => setExtensionDays(Number(event.target.value))} /><i>gün</i></div>
              </label>
              <div className="confirm-impact">
                Yalnızca bu tarihteki toplu kapanışla iptal edilen randevuların benzersiz paketleri uzatılır. Aynı kapanış ikinci kez uzatılamaz.
              </div>
            </>
          )}
          {dayActionError && <div className="form-error">{dayActionError}</div>}
          <div className="modal-actions">
            <button className="button ghost" disabled={dayActionBusy} onClick={() => setDayAction(null)}>Vazgeç</button>
            <button className={dayAction === "close" ? "button danger" : "button primary"} disabled={dayActionBusy || (dayAction === "extend" && extensionDays < 1)} onClick={async () => {
              setDayActionBusy(true);
              setDayActionError("");
              try {
                if (dayAction === "close") {
                  await closeStudioDay({ date: selectedDate, reason: closureReason.trim() || undefined });
                } else {
                  await extendStudioClosurePackages({ date: selectedDate, days: Math.min(365, Math.max(1, extensionDays)) });
                }
                setDayAction(null);
                setClosureReason("");
                setExtensionDays(1);
              } catch (actionError) {
                setDayActionError(studioErrorCopy(actionError));
              } finally {
                setDayActionBusy(false);
              }
            }}>{dayActionBusy ? "İşleniyor…" : dayAction === "close" ? "Günü kapat ve iptal et" : "Süreleri uzat"}</button>
          </div>
        </div>
      )}
      <WeeklyScheduleModal
        open={weeklyScheduleOpen}
        initialDate={selectedDate}
        appointments={appointments}
        appointmentRange={appointmentRange}
        loading={appointmentsLoading}
        onLoadRange={loadAppointmentRange}
        onClose={() => setWeeklyScheduleOpen(false)}
        onOpenAppointment={(appointment) => {
          setWeeklyScheduleOpen(false);
          onOpenAppointment(appointment);
        }}
      />
    </>
  );
}

function AppointmentsPage({
  appointments,
  onOpen,
  onNew,
  onBlock
}: {
  appointments: Appointment[];
  onOpen: (appointment: Appointment) => void;
  onNew: () => void;
  onBlock: () => void;
}) {
  const { appointmentRange, appointmentsLoading, loadAppointmentRange } = useStudio();
  const [filter, setFilter] = useState<"all" | AppointmentStatus>("all");
  const [query, setQuery] = useState("");
  const [view, setView] = useState<"list" | "month">("list");
  const [whoFilters, setWhoFilters] = useState<string[]>([]);
  const [monthCursor, setMonthCursor] = useState(() => {
    return `${istanbulToday().slice(0, 7)}-01`;
  });
  const [dateFrom, setDateFrom] = useState(appointmentRange.from);
  const [dateTo, setDateTo] = useState(appointmentRange.to);
  useEffect(() => {
    setDateFrom(appointmentRange.from);
    setDateTo(appointmentRange.to);
  }, [appointmentRange.from, appointmentRange.to]);
  const filteredAppointments = appointments.filter((appointment) => {
    const filterOk = filter === "all" || appointment.status === filter;
    const queryOk = `${appointment.customer} ${appointment.customerId} ${appointment.record ?? ""}`.toLocaleLowerCase("tr-TR").includes(query.toLocaleLowerCase("tr-TR"));
    const whoOk = !whoFilters.length || whoFilters.includes(appointment.trainer);
    return filterOk && queryOk && whoOk;
  });
  const visible = [...filteredAppointments].sort((left, right) =>
    `${right.date}T${right.time}`.localeCompare(`${left.date}T${left.time}`)
  );
  const monthDate = fromIsoDate(monthCursor);
  const monthStartDay = new Date(`${monthCursor}T12:00:00Z`).getUTCDay() || 7;
  const monthGridStart = addIsoDays(monthCursor, 1 - monthStartDay);
  const monthDays = Array.from({ length: 42 }, (_, index) => {
    const iso = addIsoDays(monthGridStart, index);
    return {
      iso,
      day: Number(iso.slice(8, 10)),
      outside: iso.slice(0, 7) !== monthCursor.slice(0, 7),
      appointments: filteredAppointments
        .filter((item) => item.date === iso)
        .sort((left, right) => left.time.localeCompare(right.time))
    };
  });
  const shiftMonth = (difference: number) => {
    setMonthCursor(`${shiftIsoMonth(monthCursor, difference).slice(0, 7)}-01`);
  };

  return (
    <>
      <PageHeading
        eyebrow="Program yönetimi"
        title="Randevular"
        description="Tüm seansları tek listede gör, planı değiştir veya yeni bir seans aç."
        action={<div className="heading-actions">
          <button className="button hold-button" onClick={onBlock}><Clock3 size={18} /> Bloke et</button>
          <button className="button primary" onClick={onNew}><Plus size={18} /> Yeni seans</button>
        </div>}
      />
      <div className="appointment-range-bar">
        <div>
          <span className="eyebrow">Yüklenen tarih aralığı</span>
          <strong>{visible.length} kayıt gösteriliyor</strong>
        </div>
        <div className="date-range">
          <label>Başlangıç<input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
          <span>→</span>
          <label>Bitiş<input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
          <button
            className="button secondary compact"
            disabled={appointmentsLoading || !dateFrom || !dateTo || dateFrom > dateTo}
            onClick={() => loadAppointmentRange({ from: dateFrom, to: dateTo })}
          >
            {appointmentsLoading ? "Yükleniyor…" : "Aralığı getir"}
          </button>
        </div>
      </div>
      <div className="toolbar">
        <div className="segmented">
          {[
            ["all", "Tümü"],
            ["planned", "Planlanan"],
            ["blocked", "Bloke"],
            ["completed", "Tamamlanan"],
            ["cancelled", "İptal"]
          ].map(([value, label]) => (
            <button
              key={value}
              className={filter === value ? "active" : ""}
              onClick={() => setFilter(value as "all" | AppointmentStatus)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="toolbar-actions">
          <label className="inline-search appointment-search"><Search size={16} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Müşteri veya kayıt ara" /></label>
          <div className="staff-filter">{[["Cansu Kara", "CK"], ["Cansu Kütük", "CKÜ"], ["Sima Nur", "SN"]].map(([name, short]) => <button key={name} className={whoFilters.includes(name) ? "active" : ""} onClick={() => setWhoFilters((items) => items.includes(name) ? items.filter((item) => item !== name) : [...items, name])}><Filter size={13} />{short}</button>)}</div>
          <div className="segmented"><button className={view === "list" ? "active" : ""} onClick={() => setView("list")}>Liste</button><button className={view === "month" ? "active" : ""} onClick={() => setView("month")}>Ay</button></div>
        </div>
      </div>
      {view === "list" ? (
        <div className="data-panel appointment-full-table">
          <div className="table-head appointment-full-grid">
            <span>No / tarih</span><span>Müşteri</span><span>Saat</span><span>Kayıt</span><span>Randevu notu</span><span>Sorumlu</span><span>Durum</span><span>Seansı yapan</span><span>Seans notu</span><span />
          </div>
          {visible.map((appointment) => (
            <button className="table-row appointment-full-grid" key={appointment.id} onClick={() => onOpen(appointment)}>
              <span className="table-time">{appointment.entryType === "hold" ? "BLOKE" : appointment.id}<small>{compactDate(appointment.date)}</small></span>
              <span className="person-cell"><i className="avatar tiny">{appointment.initials}</i><span><strong>{appointment.customer}</strong><small>{appointment.customerId}</small></span></span>
              <strong>{appointment.time}</strong><span>{appointment.entryType === "hold" ? "Paket bekliyor" : appointment.record ?? appointment.type}</span><span>{appointment.note || "—"}</span><span>{appointment.trainer}</span>
              <span><i className={`status ${appointment.status}`}>{statusLabel[appointment.status]}</i></span><span>{appointment.completedBy || "—"}</span><span>{appointment.sessionNotes || "—"}</span><span><ChevronRight size={18} /></span>
            </button>
          ))}
        </div>
      ) : (
        <div className="month-calendar">
          <div className="month-calendar-head"><button className="icon-button" onClick={() => shiftMonth(-1)}><ArrowLeft size={17} /></button><div><span className="eyebrow">{monthDate.getFullYear()}</span><h2>{new Intl.DateTimeFormat("tr-TR", { month: "long" }).format(monthDate)}</h2></div><button className="icon-button" onClick={() => shiftMonth(1)}><ArrowRight size={17} /></button></div>
          <div className="month-weekdays">{["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"].map((day) => <span key={day}>{day}</span>)}</div>
          <div className="month-days">{monthDays.map((day) => <div key={day.iso} className={`${day.iso === istanbulToday() ? "today" : ""} ${day.outside ? "outside" : ""}`}><strong>{day.day}</strong>{day.appointments.slice(0, 3).map((item) => <button key={item.id} className={item.status} onClick={() => onOpen(item)}>{item.time} · {item.customer}</button>)}{day.appointments.length > 3 && <small>+{day.appointments.length - 3} kayıt</small>}</div>)}</div>
        </div>
      )}
    </>
  );
}

function InlineNewAppointmentForm({
  sourceAppointment,
  onCancel,
  onCreated
}: {
  sourceAppointment: Appointment;
  onCancel: () => void;
  onCreated: (appointments: Appointment[]) => void;
}) {
  const { customers, records, ledger, appointments, createAppointments } = useStudio();
  const trainers = useTrainerNames();
  const customer = customers.find((item) => item.id === sourceAppointment.customerId);
  const packageOptions = getBookablePackages(customer, records, ledger, appointments);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [repeatCount, setRepeatCount] = useState("1");
  const [saving, setSaving] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const selectedPackage = packageOptions.find((item) => item.id === selectedPackageId);
  const scheduleDateValid = packageValidForSchedule(
    selectedPackage,
    appointmentDate,
    Number(repeatCount)
  );
  const conflicts = findAppointmentConflicts(
    appointments,
    appointmentDate,
    appointmentTime,
    sourceAppointment.id
  );
  const preferenceConflicts = findAppointmentPreferenceConflicts({
    selectedCustomer: customer,
    customers,
    appointments,
    date: appointmentDate,
    time: appointmentTime,
    repeatCount: Number(repeatCount),
    excludeAppointmentId: sourceAppointment.id
  });
  const hourOptions = Array.from({ length: 15 }, (_, index) => `${String(index + 7).padStart(2, "0")}:00`);

  useEffect(() => {
    const sourcePackage = packageOptions.find(
      (item) =>
        item.available > 0 &&
        (sourceAppointment.recordId
          ? item.id === sourceAppointment.recordId
          : item.detail === sourceAppointment.record)
    );
    setSelectedPackageId(sourcePackage?.id ?? packageOptions.find((item) => item.available > 0)?.id ?? packageOptions[0]?.id ?? "");
    setRepeatCount("1");
  }, [sourceAppointment.id, records, ledger, appointments]);

  useEffect(() => {
    if (selectedPackage && Number(repeatCount) > selectedPackage.available) setRepeatCount("1");
  }, [selectedPackage?.id, selectedPackage?.available, repeatCount]);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      !customer ||
      !selectedPackage ||
      selectedPackage.expired ||
      !scheduleDateValid ||
      preferenceConflicts.length > 0 ||
      selectedPackage.available < Number(repeatCount)
    ) return;
    setSaving(true);
    const data = new FormData(event.currentTarget);
    const time = appointmentTime;
    await createAppointments({
      customerId: customer.id,
      recordId: selectedPackage.id,
      date: appointmentDate,
      time,
      trainer: String(data.get("trainer")),
      note: String(data.get("note") || ""),
      repeatCount: Number(repeatCount)
    });
    onCreated([]);
    setSaving(false);
    onCancel();
  }

  return (
    <form className="completion-box drawer-inline-form" onSubmit={submit}>
      <div className="inline-form-head"><span className="eyebrow">Aynı müşteri · yeni plan</span><strong>{sourceAppointment.customer}</strong></div>
      <div className="form-row">
        <label className="field"><span>Tarih</span><input name="date" type="date" value={appointmentDate} onChange={(event) => setAppointmentDate(event.target.value)} required /></label>
        <label className="field"><span>Saat</span><select name="time" value={appointmentTime} onChange={(event) => setAppointmentTime(event.target.value)} required><option value="" disabled>Saat seçin</option>{hourOptions.map((hour) => <option key={hour}>{hour}</option>)}</select></label>
      </div>
      <TimeConflictList conflicts={conflicts} preferenceConflicts={preferenceConflicts} />
      <label className="field full">
        <span>Seans türü / kullanılacak paket</span>
        <select value={selectedPackageId} onChange={(event) => setSelectedPackageId(event.target.value)} disabled={!packageOptions.length}>
          {packageOptions.map((item) => <option value={item.id} key={item.id} disabled={item.available < 1 || item.expired}>{item.detail} · {item.remaining} kalan · {item.planned} planlı · {item.available} uygun{item.expiryDate ? ` · Son ${compactDate(item.expiryDate)}` : ""}{item.expired ? " · Süresi dolmuş" : ""}</option>)}
          {!packageOptions.length && <option>Aktif paket bulunamadı</option>}
        </select>
      </label>
      <label className="field full"><span>Sorumlu</span><select name="trainer" defaultValue={sourceAppointment.trainer}>{trainers.map((trainer) => <option key={trainer}>{trainer}</option>)}</select></label>
      <label className="field full"><span>Tekrar</span><select value={repeatCount} onChange={(event) => setRepeatCount(event.target.value)}><option value="1">Tek randevu</option><option value="4" disabled={(selectedPackage?.available ?? 0) < 4 || !packageValidForSchedule(selectedPackage, appointmentDate, 4)}>4 hafta tekrarla</option></select></label>
      <label className="field full"><span>Not <i>isteğe bağlı</i></span><textarea name="note" rows={2} placeholder="Seans öncesi bilinmesi gerekenler…" /></label>
      {selectedPackage?.expired ? (
        <div className="form-error">Bu paketin süresi {selectedPackage.expiryDate ? compactDate(selectedPackage.expiryDate) : ""} tarihinde doldu. Randevu bağlanamaz.</div>
      ) : selectedPackage && appointmentDate && !scheduleDateValid ? (
        <div className="form-error">Seçilen tarih veya tekrarların son günü paket son kullanım tarihini aşıyor.</div>
      ) : selectedPackage && selectedPackage.available > 0 ? (
        <div className="availability-note package-balance-note"><CheckCircle2 size={18} /><span><strong>{selectedPackage.remaining} hak · {selectedPackage.planned} planlı.</strong> En fazla {selectedPackage.available} yeni randevu oluşturulabilir.{selectedPackage.expiryDate ? ` Son kullanım: ${compactDate(selectedPackage.expiryDate)}.` : ""}</span></div>
      ) : selectedPackage ? (
        <div className="form-error">Bu paketteki tüm seans hakları zaten planlanmış.</div>
      ) : (
        <div className="form-error">Randevu bağlanabilecek aktif paket bulunamadı.</div>
      )}
      <div className="form-row">
        <button type="button" className="button ghost" onClick={onCancel}>Vazgeç</button>
        <button className="button primary" disabled={saving || !selectedPackage || selectedPackage.expired || !scheduleDateValid || preferenceConflicts.length > 0 || selectedPackage.available < Number(repeatCount)}>{saving ? "Kaydediliyor…" : "Randevuyu oluştur"}</button>
      </div>
    </form>
  );
}

export function HoldDrawer({
  hold,
  onClose,
  onDone
}: {
  hold: Appointment;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  useEscapeClose(onClose);
  const trainers = useTrainerNames();
  const {
    customers,
    records,
    ledger,
    appointments,
    addRecord,
    confirmHold,
    cancelHold,
    deleteHold
  } = useStudio();
  const customer = customers.find((item) => item.id === hold.customerId);
  const packageOptions = getBookablePackages(customer, records, ledger, appointments)
    .filter((item) => item.available > 0 && packageValidForSchedule(item, hold.date, 1));
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [sessionNotes, setSessionNotes] = useState("");
  const [completedBy, setCompletedBy] = useState(hold.trainer || trainers[0]);
  const [busyAction, setBusyAction] = useState<"link" | "complete" | "cancel" | "delete" | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [packageOpen, setPackageOpen] = useState(false);
  const [error, setError] = useState("");
  const packageOptionIds = packageOptions.map((item) => item.id).join("|");
  const customerRecords = records.filter((record) => record.customerId === hold.customerId);

  useEffect(() => {
    setSelectedPackageId(packageOptions[0]?.id ?? "");
    setSessionNotes("");
    setCompletedBy(hold.trainer || trainers[0]);
    setBusyAction(null);
    setConfirmCancel(false);
    setConfirmDelete(false);
    setPackageOpen(false);
    setError("");
  }, [hold.id]);

  useEffect(() => {
    if (selectedPackageId && packageOptions.some((item) => item.id === selectedPackageId)) return;
    setSelectedPackageId(packageOptions[0]?.id ?? "");
  }, [packageOptionIds, selectedPackageId]);

  async function convert(completeNow: boolean) {
    if (!selectedPackageId) return;
    setBusyAction(completeNow ? "complete" : "link");
    setError("");
    try {
      await confirmHold(
        hold,
        selectedPackageId,
        completeNow,
        sessionNotes,
        completedBy
      );
      onClose();
      onDone(
        completeNow
          ? "Bloke pakete bağlandı, seans tamamlandı ve 1 hak düşüldü."
          : "Bloke pakete bağlandı ve planlı randevuya dönüştürüldü."
      );
    } catch (convertError) {
      setError(studioErrorCopy(convertError, "Bloke seansa dönüştürülemedi. Bilgileri kontrol edin."));
    } finally {
      setBusyAction(null);
    }
  }

  async function cancel() {
    setBusyAction("cancel");
    setError("");
    try {
      await cancelHold(hold);
      onClose();
      onDone("Bloke iptal edildi; paket veya seans hakkı etkilenmedi.");
    } catch (cancelError) {
      setError(studioErrorCopy(cancelError, "Bloke iptal edilemedi. Güncel durumu kontrol edin."));
    } finally {
      setBusyAction(null);
    }
  }

  async function removePermanently() {
    setBusyAction("delete");
    setError("");
    try {
      await deleteHold(hold);
      onClose();
      onDone("Bloke takvimden tamamen silindi. Denetim kaydı korunuyor.");
    } catch (deleteError) {
      setError(studioErrorCopy(deleteError, "Bloke tamamen silinemedi. Güncel durumu kontrol edin."));
    } finally {
      setBusyAction(null);
    }
  }

  const isPending = hold.holdStatus !== "CANCELLED";

  return (
    <>
      <button className="modal-scrim" onClick={onClose} aria-label="Kapat" />
      <aside className="detail-drawer hold-drawer">
        <div className="drawer-head">
          <div><span className="eyebrow">Paket bekleyen saat</span><h2>Bloke detayı</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="Kapat"><X size={20} /></button>
        </div>
        <div className="hold-identity">
          <span className="avatar xlarge">{hold.initials}</span>
          <div><h3>{hold.customer}</h3><span>Bu saat henüz bir pakete bağlı değil.</span></div>
        </div>
        <div className="detail-grid">
          <div><span>Tarih</span><strong>{longDate(hold.date)}</strong></div>
          <div><span>Saat</span><strong>{hold.time}–{hold.endTime}</strong></div>
          <div><span>Sorumlu</span><strong>{hold.trainer}</strong></div>
          <div><span>Durum</span><strong>{isPending ? "Bloke · paket bekliyor" : "İptal edilmiş bloke"}</strong></div>
        </div>
        {hold.note && <div className="note-box"><span>Bloke notu</span><p>{hold.note}</p></div>}
        {isPending && <div className="hold-convert-panel">
          <span className="eyebrow">Danışan geldiğinde</span>
          <h3>Yeni pakete bağla</h3>
          <p>Paket şimdi açılmışsa buradan seç. Sadece bağlamak hakkı düşürmez; “Bağla ve tamamla” bir seansı kullanır.</p>
          <button type="button" className="button secondary wide hold-package-trigger" onClick={() => setPackageOpen((open) => !open)}>
            <PackagePlus size={17} /> {packageOpen ? "Paket oluşturmayı kapat" : "Müşteriye yeni paket aç"}
          </button>
          {packageOpen && (
            customer && <div className="hold-package-builder">
              <PackageCreationForm
                customer={customer}
                customerRecords={customerRecords}
                compact
                onSave={async (record) => {
                  await addRecord(record);
                  setPackageOpen(false);
                  onDone("Yeni paket oluşturuldu. Aşağıdan seçip blokeye bağlayabilirsiniz.");
                }}
              />
            </div>
          )}
          <label className="field full">
            <span>Kullanılacak paket</span>
            <select value={selectedPackageId} onChange={(event) => setSelectedPackageId(event.target.value)} disabled={!packageOptions.length}>
              {packageOptions.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.detail} · {item.remaining} kalan · {item.planned} planlı · {item.available} uygun
                </option>
              ))}
              {!packageOptions.length && <option>Uygun aktif paket bulunamadı</option>}
            </select>
          </label>
          <label className="field full"><span>Seansı yapan</span><select value={completedBy} onChange={(event) => setCompletedBy(event.target.value)}>{trainers.map((trainer) => <option key={trainer}>{trainer}</option>)}</select></label>
          <label className="field full"><span>Tamamlanma notu <i>isteğe bağlı</i></span><textarea rows={3} value={sessionNotes} onChange={(event) => setSessionNotes(event.target.value)} /></label>
          {!packageOptions.length && !packageOpen && <div className="availability-note"><PackagePlus size={17} /><span>Uygun aktif paket yok. Yukarıdaki düğmeden burada yeni paket açabilirsiniz.</span></div>}
          <div className="hold-action-stack">
            <button className="button secondary" disabled={!selectedPackageId || busyAction !== null} onClick={() => convert(false)}>{busyAction === "link" ? "Bağlanıyor…" : "Pakete bağla"}</button>
            <button className="button primary" disabled={!selectedPackageId || busyAction !== null} onClick={() => convert(true)}>{busyAction === "complete" ? "Tamamlanıyor…" : "Bağla ve tamamla"}</button>
          </div>
        </div>}
        {error && <div className="form-error">{error}</div>}
        {isPending && <button className="text-button danger-text hold-cancel-trigger" onClick={() => { setConfirmCancel(true); setConfirmDelete(false); }} disabled={busyAction !== null}><XCircle size={16} /> Danışan gelmedi, blokeyi iptal et</button>}
        {isPending && confirmCancel && (
          <div className="drawer-confirm">
            <p>Bloke iptal edilecek. Herhangi bir paket veya seans hakkı değişmeyecek.</p>
            <div className="modal-actions">
              <button className="button ghost" onClick={() => setConfirmCancel(false)}>Vazgeç</button>
              <button className="button danger" onClick={cancel} disabled={busyAction !== null}>{busyAction === "cancel" ? "İptal ediliyor…" : "Evet, blokeyi iptal et"}</button>
            </div>
          </div>
        )}
        <button className="text-button danger-text hold-delete-trigger" onClick={() => { setConfirmDelete(true); setConfirmCancel(false); }} disabled={busyAction !== null}><Trash2 size={16} /> Blokeyi tamamen sil</button>
        {confirmDelete && (
          <div className="drawer-confirm hold-delete-confirm">
            <p>Bu bloke takvimden tamamen kaldırılacak. İşlem ekranda geri alınamaz; silme öncesi bilgiler denetim günlüğünde korunur.</p>
            <div className="modal-actions">
              <button className="button ghost" onClick={() => setConfirmDelete(false)}>Vazgeç</button>
              <button className="button danger" onClick={removePermanently} disabled={busyAction !== null}>{busyAction === "delete" ? "Siliniyor…" : "Evet, tamamen sil"}</button>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}

function AppointmentDrawer({
  appointment,
  onClose,
  onStatus,
  onDelete,
  onOpenCustomer,
  onNewAppointments,
  onHoldDone
}: {
  appointment: Appointment | null;
  onClose: () => void;
  onStatus: (
    id: string,
    status: AppointmentStatus,
    sessionNotes?: string,
    completedBy?: string,
    cancellationMode?: CancellationMode,
    cancellationReason?: string,
    extendExpiryDays?: number
  ) => void;
  onDelete: (id: string, removalNote?: string) => void;
  onOpenCustomer: (customerId: string) => void;
  onNewAppointments: (appointments: Appointment[]) => void;
  onHoldDone: (message: string) => void;
}) {
  useEscapeClose(onClose, Boolean(appointment));
  const trainers = useTrainerNames();
  const { records } = useStudio();
  const [completeMode, setCompleteMode] = useState(false);
  const [newAppointmentMode, setNewAppointmentMode] = useState(false);
  const [sessionNotes, setSessionNotes] = useState("");
  const [completedBy, setCompletedBy] = useState(trainers[0]);
  const [confirmAction, setConfirmAction] = useState<"cancel" | "delete" | null>(null);
  const [cancellationMode, setCancellationMode] = useState<CancellationMode>("customer");
  const [cancellationReason, setCancellationReason] = useState("");
  const [removalNote, setRemovalNote] = useState("");
  const [extendPackageExpiry, setExtendPackageExpiry] = useState(false);
  useEffect(() => {
    setCompleteMode(false);
    setNewAppointmentMode(false);
    setSessionNotes("");
    setCompletedBy(trainers[0]);
    setConfirmAction(null);
    setCancellationMode("customer");
    setCancellationReason("");
    setRemovalNote("");
    setExtendPackageExpiry(false);
  }, [appointment?.id]);
  if (!appointment) return null;
  if (appointment.entryType === "hold") {
    return <HoldDrawer hold={appointment} onClose={onClose} onDone={onHoldDone} />;
  }
  const appointmentRecord = records.find((record) =>
    appointment.recordId
      ? record.id === appointment.recordId
      : record.customerId === appointment.customerId &&
        record.detail === (appointment.record ?? appointment.type)
  );
  const cancellationRemaining = appointmentRecord
    ? Math.max(
        0,
        (appointmentRecord.cancellationAllowance ?? 0) -
          (appointmentRecord.cancellationsUsed ?? 0)
      )
    : 0;
  const packageRemaining = appointmentRecord?.remainingSessions ?? appointment.remainingSessions;
  const packageTotal = Math.max(appointmentRecord?.quantity ?? packageRemaining, packageRemaining, 1);
  const packageRemainingPercent = Math.min(100, Math.max(0, (packageRemaining / packageTotal) * 100));
  return (
    <>
      <button className="modal-scrim" onClick={onClose} aria-label="Kapat" />
      <aside className="detail-drawer">
        <div className="drawer-head">
          <div><span className="eyebrow">{appointment.id}</span><h2>Randevu detayı</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="Kapat"><X size={20} /></button>
        </div>
        <div className="drawer-person">
          <span className="avatar xlarge">{appointment.initials}</span>
          <div><h3>{appointment.customer}</h3><span>{appointment.customerId}</span></div>
        </div>
        <div className="detail-grid">
          <div><span>Tarih</span><strong>{longDate(appointment.date)}</strong></div>
          <div><span>Saat</span><strong>{appointment.time}–{appointment.endTime}</strong></div>
          <div><span>Seans</span><strong>{appointment.type}</strong></div>
          <div><span>Eğitmen</span><strong>{appointment.trainer}</strong></div>
          <div><span>Paket / ders</span><strong>{appointment.record ?? appointment.type}</strong></div>
          <div><span>Seansı yapan</span><strong>{appointment.completedBy ?? "Henüz tamamlanmadı"}</strong></div>
        </div>
        {appointment.note && <div className="note-box"><span>Seans notu</span><p>{appointment.note}</p></div>}
        {appointment.sessionNotes && <div className="note-box session-note"><span>Tamamlanma notu</span><p>{appointment.sessionNotes}</p></div>}
        <div className="session-balance">
          <div><span>Kalan seans hakkı</span><strong>{packageRemaining}<small> / {packageTotal}</small></strong></div>
          <div className="mini-progress" aria-label={`${packageTotal} hakkın ${packageRemaining} tanesi kaldı`}><span style={{ width: `${packageRemainingPercent}%` }} /></div>
        </div>
        <div className="drawer-actions">
          {!newAppointmentMode && !completeMode && <button className="button secondary wide" onClick={() => setNewAppointmentMode(true)}>
            <Plus size={18} /> Bu müşteri için yeni randevu
          </button>}
          {newAppointmentMode && <InlineNewAppointmentForm sourceAppointment={appointment} onCancel={() => setNewAppointmentMode(false)} onCreated={onNewAppointments} />}
          {completeMode && (
            <div className="completion-box">
              <label className="field full"><span>Seans notu</span><textarea rows={3} value={sessionNotes} onChange={(event) => setSessionNotes(event.target.value)} placeholder="Seans gözlemleri ve notlar…" /></label>
              <label className="field full"><span>Seansı yapan</span><select value={completedBy} onChange={(event) => setCompletedBy(event.target.value)}>{trainers.map((trainer) => <option key={trainer}>{trainer}</option>)}</select></label>
              <div className="availability-note"><CheckCircle2 size={18} /><span><strong>Ledger işlemi:</strong> Seans tamamlanınca ilgili paket hakkı 1 azalacak ve “A{appointment.id.replace(/\D/g, "")}” hareketi oluşacak.</span></div>
              <div className="form-row"><button className="button ghost" onClick={() => setCompleteMode(false)}>Vazgeç</button><button className="button primary" onClick={() => onStatus(appointment.id, "completed", sessionNotes, completedBy)}>Tamamlamayı onayla</button></div>
            </div>
          )}
          {appointment.status === "planned" && !completeMode && !newAppointmentMode && (
            <button className="button primary wide" onClick={() => { setNewAppointmentMode(false); setCompleteMode(true); }}>
              <CheckCircle2 size={18} /> Seansı tamamla
            </button>
          )}
          {appointment.status === "planned" && !newAppointmentMode && (
            <button className="button danger wide" onClick={() => setConfirmAction("cancel")}>
              <XCircle size={18} /> Randevuyu iptal et
            </button>
          )}
          {appointment.status !== "planned" && !newAppointmentMode && (
            <button className="button ghost wide" onClick={() => onStatus(appointment.id, "planned")}>
              Randevuyu yeniden aç
            </button>
          )}
          {!newAppointmentMode && <button className="button ghost wide danger-text" onClick={() => setConfirmAction("delete")}>
            <Trash2 size={17} /> Randevuyu kaldır
          </button>}
          {!newAppointmentMode && <button className="button secondary wide" onClick={() => onOpenCustomer(appointment.customerId)}>
            <UserRound size={17} /> Müşteri kartını aç
          </button>}
        </div>
      </aside>
      {confirmAction && (
        <div className={`confirm-popover ${confirmAction === "cancel" ? "cancellation-popover" : ""}`} role="alertdialog" aria-modal="true">
          <div className="confirm-icon"><XCircle size={24} /></div>
          <h3>{confirmAction === "cancel" ? "Randevu iptal edilsin mi?" : "Randevu programdan kaldırılsın mı?"}</h3>
          <p>
            {appointment.customer} · {appointment.date} · {appointment.time}
            {confirmAction === "delete" && " kaydı programdan kaldırılacak; işlem geçmişi denetim kaydı olarak korunacak."}
          </p>
          {confirmAction === "cancel" && (
            <>
              <div className="cancellation-choice-grid">
                <button
                  type="button"
                  className={cancellationMode === "customer" ? "selected" : ""}
                  onClick={() => setCancellationMode("customer")}
                >
                  <UserRound size={19} />
                  <span>
                    <strong>Müşteri iptali</strong>
                    <small>Normal iptal kuralı uygulanır.</small>
                  </span>
                </button>
                <button
                  type="button"
                  className={cancellationMode === "studio" ? "selected protected" : "protected"}
                  onClick={() => setCancellationMode("studio")}
                >
                  <LockKeyhole size={19} />
                  <span>
                    <strong>Hakkını koruyarak iptal et</strong>
                    <small>Stüdyo veya çalışan kaynaklı iptal.</small>
                  </span>
                </button>
              </div>
              <label className="field full cancellation-reason-field">
                <span>İptal açıklaması <i>isteğe bağlı</i></span>
                <textarea
                  name="cancellationReason"
                  rows={2}
                  value={cancellationReason}
                  onChange={(event) => setCancellationReason(event.target.value)}
                  maxLength={500}
                  placeholder={cancellationMode === "studio" ? "Örn. Eğitmen program değişikliği" : "Örn. Sağlık mazereti"}
                />
              </label>
              {cancellationMode === "studio" && (
                <>
                  <label className={`expiry-extension-choice ${extendPackageExpiry ? "selected" : ""}`}>
                    <input type="checkbox" checked={extendPackageExpiry} onChange={(event) => setExtendPackageExpiry(event.target.checked)} />
                    <span><strong>Paket bitişine 7 gün ekle</strong><small>Stüdyo kaynaklı kaybı telafi etmek için ilgili paketin son kullanım tarihi 7 gün uzatılır.</small></span>
                  </label>
                </>
              )}
              <div className={`confirm-impact ${cancellationMode === "studio" ? "protected" : cancellationRemaining > 0 ? "allowance" : "session"}`}>
                {cancellationMode === "studio"
                  ? `Randevu iptal edilir; müşterinin iptal hakkı ve kalan seans bakiyesi değişmez.${extendPackageExpiry ? " Paket süresi 7 gün uzatılır." : " Paket süresi değişmez."}`
                  : cancellationRemaining > 0
                    ? `Bu işlem 1 iptal hakkı kullanır. İşlemden sonra ${cancellationRemaining - 1} iptal hakkı kalır.`
                    : "Bu pakette iptal hakkı kalmadı. Randevu iptal edilirse 1 seans hakkı yakılır."}
              </div>
            </>
          )}
          {confirmAction === "delete" && (
            <label className="field full cancellation-reason-field">
              <span>İç işlem notu <i>isteğe bağlı</i></span>
              <textarea
                name="removalNote"
                rows={2}
                value={removalNote}
                onChange={(event) => setRemovalNote(event.target.value)}
                maxLength={500}
                placeholder="Örn. Hatalı tarih seçimi"
              />
              <small>Bu not yalnızca iç işlem geçmişinde saklanır; müşteriye gönderilmez.</small>
            </label>
          )}
          <div className="modal-actions">
            <button className="button ghost" onClick={() => setConfirmAction(null)}>Vazgeç</button>
            <button
              className="button danger"
              onClick={() =>
                confirmAction === "cancel"
                  ? onStatus(
                      appointment.id,
                      "cancelled",
                      undefined,
                      undefined,
                      cancellationMode,
                      cancellationReason.trim() || undefined,
                      cancellationMode === "studio" && extendPackageExpiry ? 7 : 0
                    )
                  : onDelete(appointment.id, removalNote.trim() || undefined)
              }
            >
              {confirmAction === "cancel"
                ? cancellationMode === "studio"
                  ? "Hakkı koruyarak iptal et"
                  : "Müşteri iptalini onayla"
                : "Evet, kaldır"}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

function NewHoldModal({
  open,
  onClose,
  onCreated,
  initialDate
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  initialDate?: string | null;
}) {
  useEscapeClose(onClose, open);
  const trainers = useTrainerNames();
  const { customers, createHold } = useStudio();
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [holdDate, setHoldDate] = useState("");
  const [holdTime, setHoldTime] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const customerPickerRef = useRef<HTMLLabelElement>(null);
  const selectedCustomer = customers.find((customer) => customer.id === selectedCustomerId);
  const normalizedSearch = customerSearch.toLocaleLowerCase("tr-TR");
  const matchingCustomers = customers
    .filter((customer) =>
      `${customer.name} ${customer.id} ${customer.phone}`
        .toLocaleLowerCase("tr-TR")
        .includes(normalizedSearch)
    )
    .slice(0, 30);
  const hourOptions = Array.from(
    { length: 15 },
    (_, index) => `${String(index + 7).padStart(2, "0")}:00`
  );

  useEffect(() => {
    if (!open) return;
    setCustomerSearch("");
    setSelectedCustomerId("");
    setPickerOpen(false);
    setHoldDate("");
    setHoldTime("");
    setError("");
  }, [open, initialDate]);

  useEffect(() => {
    if (!open || !pickerOpen) return;
    const closePicker = (event: PointerEvent) => {
      if (!customerPickerRef.current?.contains(event.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("pointerdown", closePicker);
    return () => document.removeEventListener("pointerdown", closePicker);
  }, [open, pickerOpen]);

  if (!open) return null;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedCustomer) return;
    setSaving(true);
    setError("");
    const data = new FormData(event.currentTarget);
    try {
      await createHold({
        customerId: selectedCustomer.id,
        date: holdDate,
        time: holdTime,
        trainer: String(data.get("trainer") || ""),
        note: String(data.get("note") || "")
      });
      onCreated();
    } catch (createError) {
      setError(studioErrorCopy(createError, "Bloke oluşturulamadı. Bilgileri kontrol edin."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button className="modal-scrim" onClick={onClose} aria-label="Kapat" />
      <div className="modal-card hold-modal" role="dialog" aria-modal="true" aria-labelledby="new-hold-title">
        <div className="drawer-head">
          <div><span className="eyebrow">Paket açmadan yer ayır</span><h2 id="new-hold-title">Yeni bloke</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="Kapat"><X size={20} /></button>
        </div>
        <div className="hold-explainer">
          <Clock3 size={20} />
          <span><strong>Bu işlem seans hakkı kullanmaz.</strong> Danışan geldiğinde blokeyi yeni paketine bağlayabilir veya gelmezse iptal edebilirsin.</span>
        </div>
        <form onSubmit={submit}>
          <label className="field full customer-combobox" ref={customerPickerRef}>
            <span>Müşteri</span>
            <div className="customer-search-control">
              <Search size={17} />
              <input
                value={customerSearch}
                onFocus={() => setPickerOpen(true)}
                onChange={(event) => {
                  setCustomerSearch(event.target.value);
                  setSelectedCustomerId("");
                  setPickerOpen(true);
                }}
                placeholder="İsim, telefon veya müşteri koduyla ara"
                autoComplete="off"
              />
              {selectedCustomer && <CheckCircle2 size={17} className="selected-check" />}
            </div>
            {pickerOpen && (
              <div className="customer-picker-list">
                {matchingCustomers.map((customer) => (
                  <button
                    type="button"
                    key={customer.id}
                    onClick={() => {
                      setSelectedCustomerId(customer.id);
                      setCustomerSearch(customer.name);
                      setPickerOpen(false);
                    }}
                  >
                    <span className="avatar tiny">{customer.initials}</span>
                    <span><strong>{customer.name}</strong><small>{customer.phone} · {customer.customerCode ?? customer.id}</small></span>
                    <em>{customer.remainingSessions} seans</em>
                  </button>
                ))}
                {!matchingCustomers.length && <div className="search-empty">Eşleşen müşteri bulunamadı.</div>}
              </div>
            )}
          </label>
          <div className="form-row">
            <label className="field"><span>Tarih</span><input type="date" value={holdDate} min={istanbulToday()} onChange={(event) => setHoldDate(event.target.value)} required /></label>
            <label className="field"><span>Saat</span><select value={holdTime} onChange={(event) => setHoldTime(event.target.value)} required><option value="" disabled>Saat seçin</option>{hourOptions.map((hour) => <option key={hour}>{hour}</option>)}</select></label>
          </div>
          <label className="field full"><span>Sorumlu</span><select name="trainer" defaultValue={trainers[0]}>{trainers.map((trainer) => <option key={trainer}>{trainer}</option>)}</select></label>
          <label className="field full"><span>Not <i>isteğe bağlı</i></span><textarea name="note" rows={3} placeholder="Örn. Pazartesi geldiğinde yeni paket açılacak." /></label>
          {error && <div className="form-error">{error}</div>}
          <div className="modal-actions">
            <button type="button" className="button ghost" onClick={onClose}>Vazgeç</button>
            <button className="button hold-button" disabled={saving || !selectedCustomer}>{saving ? "Kaydediliyor…" : "Blokeyi oluştur"}</button>
          </div>
        </form>
      </div>
    </>
  );
}

function NewAppointmentModal({
  open,
  onClose,
  onCreated,
  initialCustomerId,
  initialDate
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (appointments: Appointment[]) => void;
  initialCustomerId?: string | null;
  initialDate?: string | null;
}) {
  useEscapeClose(onClose, open);
  const trainers = useTrainerNames();
  const [saving, setSaving] = useState(false);
  const { customers, records, ledger, appointments, createAppointments } = useStudio();
  const [customerSearch, setCustomerSearch] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [repeatCount, setRepeatCount] = useState("1");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [appointmentDate, setAppointmentDate] = useState("");
  const [appointmentTime, setAppointmentTime] = useState("");
  const [error, setError] = useState("");
  const customerPickerRef = useRef<HTMLLabelElement>(null);
  useEffect(() => {
    if (!open) return;
    const initialCustomer = customers.find((customer) => customer.id === initialCustomerId);
    setSelectedCustomerId(initialCustomer?.id ?? "");
    setCustomerSearch(initialCustomer?.name ?? "");
    setRepeatCount("1");
    setPickerOpen(false);
    setAppointmentDate("");
    setAppointmentTime("");
    setError("");
  }, [open, initialCustomerId, initialDate]);
  useEffect(() => {
    if (!open || !pickerOpen) return;
    const closePicker = (event: PointerEvent) => {
      if (!customerPickerRef.current?.contains(event.target as Node)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("pointerdown", closePicker);
    return () => document.removeEventListener("pointerdown", closePicker);
  }, [open, pickerOpen]);
  const selectedCustomer = customers.find((customer) => customer.id === selectedCustomerId);
  const availablePackages = getBookablePackages(selectedCustomer, records, ledger, appointments);
  useEffect(() => {
    if (!selectedCustomerId) {
      setSelectedPackageId("");
      return;
    }
    setSelectedPackageId((current) => {
      const currentPackage = availablePackages.find((item) => item.id === current);
      if (currentPackage?.available) return current;
      return availablePackages.find((item) => item.available > 0)?.id ?? availablePackages[0]?.id ?? "";
    });
  }, [selectedCustomerId, records, ledger, appointments]);
  const selectedPackage = availablePackages.find((record) => record.id === selectedPackageId);
  const scheduleDateValid = packageValidForSchedule(
    selectedPackage,
    appointmentDate,
    Number(repeatCount)
  );
  const conflicts = findAppointmentConflicts(appointments, appointmentDate, appointmentTime);
  const preferenceConflicts = findAppointmentPreferenceConflicts({
    selectedCustomer,
    customers,
    appointments,
    date: appointmentDate,
    time: appointmentTime,
    repeatCount: Number(repeatCount)
  });
  useEffect(() => {
    if (selectedPackage && Number(repeatCount) > selectedPackage.available) setRepeatCount("1");
  }, [selectedPackage?.id, selectedPackage?.available, repeatCount]);
  const normalizedSearch = customerSearch.toLocaleLowerCase("tr-TR");
  const matchingCustomers = customers
    .filter((customer) =>
      `${customer.name} ${customer.id} ${customer.phone}`.toLocaleLowerCase("tr-TR").includes(normalizedSearch)
    )
    .slice(0, 30);
  const hourOptions = Array.from({ length: 15 }, (_, index) => `${String(index + 7).padStart(2, "0")}:00`);
  if (!open) return null;

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (
      !selectedCustomer ||
      !selectedPackage ||
      selectedPackage.expired ||
      !scheduleDateValid ||
      preferenceConflicts.length > 0 ||
      selectedPackage.available < Number(repeatCount)
    ) return;
    setSaving(true);
    setError("");
    const data = new FormData(event.currentTarget);
    const time = appointmentTime;
    try {
      await createAppointments({
        customerId: selectedCustomer.id,
        recordId: selectedPackage.id,
        date: appointmentDate,
        time,
        trainer: String(data.get("trainer")),
        note: String(data.get("note") || ""),
        repeatCount: Number(repeatCount)
      });
      onCreated([]);
    } catch (createError) {
      setError(studioErrorCopy(createError, "Seans oluşturulamadı. Bilgileri kontrol edin."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <button className="modal-scrim" onClick={onClose} aria-label="Kapat" />
      <div className="modal-card" role="dialog" aria-modal="true" aria-labelledby="new-appointment-title">
        <div className="drawer-head">
          <div><span className="eyebrow">Programı güncelle</span><h2 id="new-appointment-title">Yeni seans</h2></div>
          <button className="icon-button" onClick={onClose} aria-label="Kapat"><X size={20} /></button>
        </div>
        <form onSubmit={submit}>
          <label className="field full customer-combobox" ref={customerPickerRef}>
            <span>Müşteri</span>
            <div className="customer-search-control">
              <Search size={17} />
              <input
                value={customerSearch}
                onFocus={() => setPickerOpen(true)}
                onChange={(event) => {
                  setCustomerSearch(event.target.value);
                  setSelectedCustomerId("");
                  setPickerOpen(true);
                }}
                placeholder="İsim, telefon veya müşteri koduyla ara"
                autoComplete="off"
              />
              {selectedCustomer && <CheckCircle2 size={17} className="selected-check" />}
            </div>
            {pickerOpen && (
              <div className="customer-picker-list">
                {matchingCustomers.map((customer) => (
                  <button
                    type="button"
                    key={customer.id}
                    className={customer.id === selectedCustomerId ? "active" : ""}
                    onClick={() => {
                      setSelectedCustomerId(customer.id);
                      setCustomerSearch(customer.name);
                      setSelectedPackageId("");
                      setPickerOpen(false);
                    }}
                  >
                    <span className="avatar tiny">{customer.initials}</span>
                    <span><strong>{customer.name}</strong><small>{customer.phone} · {customer.id}</small></span>
                    <em>{customer.remainingSessions} seans</em>
                  </button>
                ))}
                {!matchingCustomers.length && <div className="search-empty">Eşleşen müşteri bulunamadı.</div>}
              </div>
            )}
          </label>
          <div className="form-row">
            <label className="field"><span>Tarih</span><input name="date" type="date" required value={appointmentDate} onChange={(event) => setAppointmentDate(event.target.value)} /></label>
            <label className="field"><span>Saat</span><select name="time" required value={appointmentTime} onChange={(event) => setAppointmentTime(event.target.value)}><option value="" disabled>Saat seçin</option>{hourOptions.map((hour) => <option key={hour}>{hour}</option>)}</select></label>
          </div>
          <TimeConflictList conflicts={conflicts} preferenceConflicts={preferenceConflicts} />
          <label className="field full">
            <span>Seans türü / kullanılacak paket</span>
            <select value={selectedPackageId} onChange={(event) => setSelectedPackageId(event.target.value)} disabled={!availablePackages.length}>
              {availablePackages.map((record) => (
                <option value={record.id} key={record.id} disabled={record.available < 1 || record.expired}>
                  {record.detail} · {record.remaining} kalan · {record.planned} planlı · {record.available} uygun{record.expiryDate ? ` · Son ${compactDate(record.expiryDate)}` : ""}{record.expired ? " · Süresi dolmuş" : ""}
                </option>
              ))}
              {!availablePackages.length && <option>Aktif paket bulunamadı</option>}
            </select>
          </label>
          <div className="form-row">
            <label className="field"><span>Sorumlu</span><select name="trainer" defaultValue={trainers[0]}>{trainers.map((trainer) => <option key={trainer}>{trainer}</option>)}</select></label>
            <label className="field"><span>Tekrar</span><select name="repeat" value={repeatCount} onChange={(event) => setRepeatCount(event.target.value)}><option value="1">Tek randevu</option><option value="4" disabled={(selectedPackage?.available ?? 0) < 4 || !packageValidForSchedule(selectedPackage, appointmentDate, 4)}>4 hafta tekrarla</option></select></label>
          </div>
          <label className="field full"><span>Not <i>isteğe bağlı</i></span><textarea name="note" rows={3} placeholder="Seans öncesi bilinmesi gerekenler…" /></label>
          {selectedPackage?.expired ? (
            <div className="form-error">Bu paketin süresi {selectedPackage.expiryDate ? compactDate(selectedPackage.expiryDate) : ""} tarihinde doldu. Randevu bağlanamaz.</div>
          ) : selectedPackage && appointmentDate && !scheduleDateValid ? (
            <div className="form-error">Seçilen tarih veya tekrarların son günü paket son kullanım tarihini aşıyor.</div>
          ) : selectedPackage && selectedPackage.available > 0 ? (
            <div className="availability-note package-balance-note"><CheckCircle2 size={18} /><span><strong>{selectedPackage.remaining} hak · {selectedPackage.planned} planlı · {selectedPackage.available} yeni randevu uygun.</strong> Randevu {selectedPackage.detail} paketine bağlanacak.{selectedPackage.expiryDate ? ` Son kullanım: ${compactDate(selectedPackage.expiryDate)}.` : ""}</span></div>
          ) : selectedPackage ? (
            <div className="form-error">Bu pakette {selectedPackage.remaining} seans hakkının tamamı planlanmış. Yeni randevu oluşturulamaz.</div>
          ) : (
            <div className="form-error">Randevu oluşturmak için aktif seans hakkı olan bir müşteri seçin.</div>
          )}
          {error && <div className="form-error">{error}</div>}
          <div className="modal-actions">
            <button type="button" className="button ghost" onClick={onClose}>Vazgeç</button>
            <button type="submit" className="button primary" disabled={saving || !selectedCustomer || !selectedPackage || selectedPackage.expired || !scheduleDateValid || preferenceConflicts.length > 0 || selectedPackage.available < Number(repeatCount)}>{saving ? "Kaydediliyor…" : "Seansı oluştur"}</button>
          </div>
        </form>
      </div>
    </>
  );
}

function FinanceGate({ onUnlock }: { onUnlock: () => void }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  return (
    <section className="finance-gate">
      <span className="gate-mark"><LockKeyhole size={26} /></span>
      <span className="eyebrow">Yetkili alan</span>
      <h1>Kasa & paketler kilitli</h1>
      <p>Tahsilat ve gider bilgilerini görmek için yönetici şifresini girin.</p>
      <form onSubmit={async (event) => {
        event.preventDefault();
        setChecking(true);
        setError("");
        try {
          const response = await fetch("/api/finance/unlock", {
            method: "POST",
            cache: "no-store",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ pin: password })
          });
          if (response.ok) {
            setPassword("");
            onUnlock();
            return;
          }
          const body = await response.json().catch(() => ({})) as { error?: string };
          setError(
            body.error === "FINANCE_PIN_NOT_CONFIGURED"
              ? "Kasa erişimi henüz sunucuda yapılandırılmamış."
              : body.error === "FINANCE_PIN_RATE_LIMITED"
                ? "Çok sayıda hatalı deneme yapıldı. Güvenlik için 10 dakika sonra tekrar deneyin."
              : "Şifre doğru değil. Tekrar deneyin."
          );
        } catch {
          setError("Kasa doğrulama servisine ulaşılamadı. Bağlantıyı kontrol edin.");
        } finally {
          setChecking(false);
        }
      }}>
        <label className="field full"><span>Yönetici şifresi</span><input type="password" inputMode="numeric" autoComplete="off" value={password} onChange={(event) => setPassword(event.target.value)} autoFocus /></label>
        {error && <div className="form-error">{error}</div>}
        <button className="button primary wide" disabled={checking || !password}>{checking ? "Doğrulanıyor…" : "Kasayı aç"}</button>
      </form>
    </section>
  );
}

function SettingsDrawer({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  useEscapeClose(onClose);
  const { packageRules, workspaceSettings, saveWorkspaceSettings } = useStudio();
  const [reminders, setReminders] = useState(workspaceSettings.sessionReminders);
  const [cancelWarnings, setCancelWarnings] = useState(workspaceSettings.cancellationConfirmations);
  const [compactView, setCompactView] = useState(workspaceSettings.compactView);
  const [eightValidity, setEightValidity] = useState(packageRules.eight.validityDays);
  const [eightAlternativeValidity, setEightAlternativeValidity] = useState(packageRules.eight.alternativeValidityDays);
  const [eightCancellations, setEightCancellations] = useState(packageRules.eight.cancellationAllowance);
  const [sixteenValidity, setSixteenValidity] = useState(packageRules.sixteen.validityDays);
  const [sixteenAlternativeValidity, setSixteenAlternativeValidity] = useState(packageRules.sixteen.alternativeValidityDays);
  const [sixteenCancellations, setSixteenCancellations] = useState(packageRules.sixteen.cancellationAllowance);
  const [specialFiveValidity, setSpecialFiveValidity] = useState(packageRules.manualSchrothFive.validityDays);
  const [specialFiveAlternativeValidity, setSpecialFiveAlternativeValidity] = useState(packageRules.manualSchrothFive.alternativeValidityDays);
  const [specialTenValidity, setSpecialTenValidity] = useState(packageRules.manualSchrothTen.validityDays);
  const [specialTenAlternativeValidity, setSpecialTenAlternativeValidity] = useState(packageRules.manualSchrothTen.alternativeValidityDays);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  return (
    <>
      <button className="modal-scrim" onClick={onClose} aria-label="Ayarları kapat" />
      <aside className="detail-drawer utility-drawer">
        <div className="drawer-head"><div><span className="eyebrow">THH Portal</span><h2>Ayarlar</h2></div><button className="icon-button" onClick={onClose} aria-label="Ayarları kapat"><X size={20} /></button></div>
        <div className="settings-section">
          <h3>Randevu davranışları</h3>
          <label className="switch-row"><span><strong>Seans hatırlatmaları</strong><small>Yarının planlı seanslarını bildirim merkezinde göster.</small></span><input type="checkbox" checked={reminders} onChange={(event) => setReminders(event.target.checked)} /></label>
          <label className="switch-row"><span><strong>İptal uyarısı</strong><small>İptal işleminden önce onay iste.</small></span><input type="checkbox" checked={cancelWarnings} onChange={(event) => setCancelWarnings(event.target.checked)} /></label>
          <label className="switch-row"><span><strong>Sıkışık liste görünümü</strong><small>Geniş ekranlarda daha fazla satır göster.</small></span><input type="checkbox" checked={compactView} onChange={(event) => setCompactView(event.target.checked)} /></label>
        </div>
        <div className="settings-section">
          <h3>Paket kuralları</h3>
          <p className="settings-help">Bu değerler yeni satılan paketlere uygulanır. Mevcut paketlerin kazanılmış koşulları değişmez.</p>
          <div className="package-rule-grid">
            <section>
              <span className="rule-badge">8 seans</span>
              <label className="field full"><span>1. süre seçeneği</span><div className="suffix-input"><input type="number" min="1" max="730" value={eightValidity} onChange={(event) => setEightValidity(Number(event.target.value))} /><i>gün</i></div></label>
              <label className="field full"><span>2. süre seçeneği</span><div className="suffix-input"><input type="number" min="1" max="730" value={eightAlternativeValidity} onChange={(event) => setEightAlternativeValidity(Number(event.target.value))} /><i>gün</i></div></label>
              <label className="field full"><span>İptal hakkı</span><div className="suffix-input"><input type="number" min="0" max="8" value={eightCancellations} onChange={(event) => setEightCancellations(Number(event.target.value))} /><i>adet</i></div></label>
            </section>
            <section>
              <span className="rule-badge">16 seans</span>
              <label className="field full"><span>1. süre seçeneği</span><div className="suffix-input"><input type="number" min="1" max="730" value={sixteenValidity} onChange={(event) => setSixteenValidity(Number(event.target.value))} /><i>gün</i></div></label>
              <label className="field full"><span>2. süre seçeneği</span><div className="suffix-input"><input type="number" min="1" max="730" value={sixteenAlternativeValidity} onChange={(event) => setSixteenAlternativeValidity(Number(event.target.value))} /><i>gün</i></div></label>
              <label className="field full"><span>İptal hakkı</span><div className="suffix-input"><input type="number" min="0" max="16" value={sixteenCancellations} onChange={(event) => setSixteenCancellations(Number(event.target.value))} /><i>adet</i></div></label>
            </section>
            <section>
              <span className="rule-badge">Manuel / Schroth · 5 seans</span>
              <label className="field full"><span>1. süre seçeneği</span><div className="suffix-input"><input type="number" min="1" max="730" value={specialFiveValidity} onChange={(event) => setSpecialFiveValidity(Number(event.target.value))} /><i>gün</i></div></label>
              <label className="field full"><span>2. süre seçeneği</span><div className="suffix-input"><input type="number" min="1" max="730" value={specialFiveAlternativeValidity} onChange={(event) => setSpecialFiveAlternativeValidity(Number(event.target.value))} /><i>gün</i></div></label>
            </section>
            <section>
              <span className="rule-badge">Manuel / Schroth · 10 seans</span>
              <label className="field full"><span>1. süre seçeneği</span><div className="suffix-input"><input type="number" min="1" max="730" value={specialTenValidity} onChange={(event) => setSpecialTenValidity(Number(event.target.value))} /><i>gün</i></div></label>
              <label className="field full"><span>2. süre seçeneği</span><div className="suffix-input"><input type="number" min="1" max="730" value={specialTenAlternativeValidity} onChange={(event) => setSpecialTenAlternativeValidity(Number(event.target.value))} /><i>gün</i></div></label>
            </section>
          </div>
        </div>
        <div className="settings-section">
          <h3>Seans standardı</h3>
          <div className="detail-grid"><div><span>Başlangıç</span><strong>Tam saat</strong></div><div><span>Seans süresi</span><strong>60 dakika</strong></div></div>
        </div>
        {error && <div className="form-error">{error}</div>}
        <button className="button primary wide" disabled={saving} onClick={async () => {
          setSaving(true);
          setError("");
          try {
            await saveWorkspaceSettings({
              sessionReminders: reminders,
              cancellationConfirmations: cancelWarnings,
              compactView
            }, {
              eight: {
                validityDays: Math.min(730, Math.max(1, eightValidity)),
                alternativeValidityDays: Math.min(730, Math.max(1, eightAlternativeValidity)),
                cancellationAllowance: Math.min(8, Math.max(0, eightCancellations))
              },
              sixteen: {
                validityDays: Math.min(730, Math.max(1, sixteenValidity)),
                alternativeValidityDays: Math.min(730, Math.max(1, sixteenAlternativeValidity)),
                cancellationAllowance: Math.min(16, Math.max(0, sixteenCancellations))
              },
              manualSchrothFive: {
                validityDays: Math.min(730, Math.max(1, specialFiveValidity)),
                alternativeValidityDays: Math.min(730, Math.max(1, specialFiveAlternativeValidity))
              },
              manualSchrothTen: {
                validityDays: Math.min(730, Math.max(1, specialTenValidity)),
                alternativeValidityDays: Math.min(730, Math.max(1, specialTenAlternativeValidity))
              }
            });
            onSaved();
            onClose();
          } catch (saveError) {
            setError(saveError instanceof Error ? `Ayarlar kaydedilemedi: ${saveError.message}` : "Ayarlar kaydedilemedi.");
          } finally {
            setSaving(false);
          }
        }}>{saving ? "Ayarlar kaydediliyor…" : "Ayarları kaydet"}</button>
      </aside>
    </>
  );
}

function ProfileDrawer({
  onClose,
  onLockFinance,
  onToast
}: {
  onClose: () => void;
  onLockFinance: () => void;
  onToast: (message: string) => void;
}) {
  useEscapeClose(onClose);
  const logout = async () => {
    await fetch("/api/auth/logout", { method: "POST" }).catch(() => null);
    window.location.replace("/login");
  };
  return (
    <>
      <button className="modal-scrim" onClick={onClose} aria-label="Profili kapat" />
      <aside className="detail-drawer utility-drawer">
        <div className="drawer-head"><div><span className="eyebrow">Kullanıcı hesabı</span><h2>Profil</h2></div><button className="icon-button" onClick={onClose} aria-label="Profili kapat"><X size={20} /></button></div>
        <div className="profile-hero"><span className="avatar xlarge">THH</span><div><h3>THH Portal</h3><span>Paylaşımlı çalışma istasyonu</span></div></div>
        <div className="detail-grid"><div><span>Şirket</span><strong>The Heal Hub</strong></div><div><span>Kullanım</span><strong>Ortak personel ekranı</strong></div><div><span>Oturum</span><strong>Sürekli açık</strong></div><div><span>Sistem durumu</span><strong>Güncel</strong></div></div>
        <div className="drawer-actions">
          <button className="button secondary wide" onClick={() => { onToast("Profil bilgileri güncel."); onClose(); }}>Profil bilgilerini kontrol et</button>
          <button className="button ghost wide" onClick={() => { onLockFinance(); onToast("Kasa yeniden kilitlendi."); onClose(); }}><LockKeyhole size={17} /> Kasayı kilitle</button>
          <button className="button ghost wide" onClick={logout}><LogOut size={17} /> Portal oturumunu kapat</button>
        </div>
      </aside>
    </>
  );
}

function MobileNav({
  page,
  onNavigate,
  onMore
}: {
  page: Page;
  onNavigate: (page: Page) => void;
  onMore: () => void;
}) {
  const items = navItems.filter((item) =>
    ["dashboard", "appointments", "customers", "finance"].includes(item.id)
  );
  return (
    <nav className="mobile-nav" aria-label="Mobil menü">
      {items.map(({ id, label, icon: Icon }) => (
        <button key={id} className={page === id ? "active" : ""} onClick={() => onNavigate(id)}>
          <Icon size={20} /><span>{label.split(" ")[0]}</span>
        </button>
      ))}
      <button onClick={onMore}><Menu size={20} /><span>Daha fazla</span></button>
    </nav>
  );
}

function OperationFeedback({ state }: { state: OperationState | null }) {
  if (!state) return null;
  return (
    <div
      className={`operation-feedback ${state.phase}`}
      role={state.phase === "error" ? "alert" : "status"}
      aria-live="polite"
      aria-atomic="true"
    >
      <span className="operation-feedback-icon" aria-hidden="true">
        {state.phase === "working"
          ? <Activity size={18} />
          : state.phase === "success"
            ? <CheckCircle2 size={18} />
            : <XCircle size={18} />}
      </span>
      <span>
        <strong>{state.phase === "working" ? "İşlem sürüyor" : state.phase === "success" ? "Tamamlandı" : "İşlem tamamlanamadı"}</strong>
        <small>{state.message}</small>
      </span>
    </div>
  );
}

export default function App() {
  const [page, setPage] = useState<Page>("dashboard");
  const {
    customers,
    appointments,
    records,
    setAppointments,
    setCustomers,
    setLedger,
    adjustCancellationUsage,
    setAppointmentStatus,
    voidAppointment,
    connectionState,
    connectionError,
    operationState,
    workspaceSettings,
    refresh
  } = useStudio();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newAppointmentOpen, setNewAppointmentOpen] = useState(false);
  const [newHoldOpen, setNewHoldOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const [customerFocusId, setCustomerFocusId] = useState<string | null>(null);
  const [quickCustomerId, setQuickCustomerId] = useState<string | null>(null);
  const [newAppointmentCustomerId, setNewAppointmentCustomerId] = useState<string | null>(null);
  const [newAppointmentDate, setNewAppointmentDate] = useState<string | null>(null);
  const [financeUnlocked, setFinanceUnlocked] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);

  useEffect(() => {
    if (page !== "finance") {
      setFinanceUnlocked(false);
    }
  }, [page]);

  useEffect(() => {
    if (page !== "customers") setCustomerFocusId(null);
  }, [page]);

  const selectedAppointment = useMemo(
    () => appointments.find((appointment) => appointment.id === selectedId) ?? null,
    [appointments, selectedId]
  );

  useEffect(() => {
    if ("serviceWorker" in navigator && import.meta.env.PROD) {
      let reloadingForUpdate = false;
      const reloadForUpdate = () => {
        if (reloadingForUpdate) return;
        reloadingForUpdate = true;
        window.location.reload();
      };

      navigator.serviceWorker.addEventListener("controllerchange", reloadForUpdate);
      navigator.serviceWorker
        .register("/sw.js", { updateViaCache: "none" })
        .then((registration) => registration.update())
        .catch(() => undefined);

      return () => {
        navigator.serviceWorker.removeEventListener("controllerchange", reloadForUpdate);
      };
    }
  }, []);

  function showToast(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(null), 2800);
  }

  function openCustomer(customerId: string) {
    setCustomerFocusId(customerId);
    setPage("customers");
    setGlobalSearch("");
  }

  function openCustomerOverlay(customerId: string) {
    setQuickCustomerId(customerId);
  }

  function openNewAppointment(customerId?: string, date?: string) {
    setNewAppointmentCustomerId(customerId ?? null);
    setNewAppointmentDate(date ?? null);
    setSelectedId(null);
    setNewAppointmentOpen(true);
  }

  function setStatusLegacy(id: string, status: AppointmentStatus, sessionNotes?: string, completedBy?: string) {
    const target = appointments.find((appointment) => appointment.id === id);
    const targetRecord = target
      ? records.find((record) =>
          target.recordId
            ? record.id === target.recordId
            : record.customerId === target.customerId && record.detail === (target.record ?? target.type)
        )
      : undefined;
    const cancellationRemaining = targetRecord
      ? Math.max(
          0,
          (targetRecord.cancellationAllowance ?? 0) - (targetRecord.cancellationsUsed ?? 0)
        )
      : 0;
    const cancellationOutcome: Appointment["cancellationOutcome"] =
      target && status === "cancelled" && target.status === "planned"
        ? cancellationRemaining > 0
          ? "allowance"
          : "session"
        : target?.cancellationOutcome;
    setAppointments((current) =>
      current.map((appointment) =>
        appointment.id === id
          ? {
              ...appointment,
              status,
              sessionNotes: status === "completed" ? sessionNotes : status === "planned" ? undefined : appointment.sessionNotes,
              completedBy: status === "completed" ? completedBy : status === "planned" ? undefined : appointment.completedBy,
              cancellationOutcome:
                status === "cancelled"
                  ? cancellationOutcome
                  : status === "planned"
                    ? undefined
                    : appointment.cancellationOutcome,
              remainingSessions:
                status === "completed" && appointment.status !== "completed"
                  ? Math.max(0, appointment.remainingSessions - 1)
                : status === "planned" && appointment.status === "completed"
                    ? appointment.remainingSessions + 1
                    : status === "cancelled" &&
                        appointment.status === "planned" &&
                        cancellationOutcome === "session"
                      ? Math.max(0, appointment.remainingSessions - 1)
                      : status === "planned" &&
                          appointment.status === "cancelled" &&
                          appointment.cancellationOutcome === "session"
                        ? appointment.remainingSessions + 1
                    : appointment.remainingSessions
            }
          : appointment
      )
    );
    if (target && status === "completed" && target.status !== "completed") {
      setCustomers((items) => items.map((customer) => customer.id === target.customerId ? { ...customer, remainingSessions: Math.max(0, customer.remainingSessions - 1) } : customer));
      const reference = `A${target.id.replace(/\D/g, "")}`;
      const packageReference = target.recordId ? `R${target.recordId.replace(/\D/g, "")}` : null;
      setLedger((items) => [
        {
          id: `LED-${crypto.randomUUID().slice(0, 6).toUpperCase()}`,
          description: "Session",
          customerId: target.customerId,
          customer: target.customer,
          quantity: -1,
          remainingQuantity: Math.max(0, target.remainingSessions - 1),
          relatedWith: target.record ?? target.type,
          date: target.date,
          reference
        },
        ...items.map((entry) =>
          entry.customerId === target.customerId &&
          entry.description === "Record" &&
          (packageReference
            ? entry.reference === packageReference
            : entry.relatedWith === (target.record ?? target.type))
            ? { ...entry, remainingQuantity: Math.max(0, (entry.remainingQuantity ?? 0) - 1) }
            : entry
        )
      ]);
    }
    if (target && status === "planned" && target.status === "completed") {
      setCustomers((items) => items.map((customer) => customer.id === target.customerId ? { ...customer, remainingSessions: customer.remainingSessions + 1 } : customer));
      const reference = `A${target.id.replace(/\D/g, "")}`;
      const packageReference = target.recordId ? `R${target.recordId.replace(/\D/g, "")}` : null;
      setLedger((items) =>
        items
          .filter((entry) => entry.reference !== reference)
          .map((entry) =>
            entry.customerId === target.customerId &&
            entry.description === "Record" &&
            (packageReference
              ? entry.reference === packageReference
              : entry.relatedWith === (target.record ?? target.type))
              ? { ...entry, remainingQuantity: (entry.remainingQuantity ?? 0) + 1 }
              : entry
          )
      );
    }
    if (target && status === "cancelled" && target.status === "planned" && targetRecord) {
      if (cancellationOutcome === "allowance") {
        adjustCancellationUsage(targetRecord.id, 1);
      } else {
        setCustomers((items) =>
          items.map((customer) =>
            customer.id === target.customerId
              ? { ...customer, remainingSessions: Math.max(0, customer.remainingSessions - 1) }
              : customer
          )
        );
        const reference = `C${target.id.replace(/\D/g, "")}`;
        const packageReference = `R${targetRecord.id.replace(/\D/g, "")}`;
        setLedger((items) => [
          {
            id: `LED-${crypto.randomUUID().slice(0, 6).toUpperCase()}`,
            description: "Session",
            customerId: target.customerId,
            customer: target.customer,
            quantity: -1,
            remainingQuantity: Math.max(0, target.remainingSessions - 1),
            relatedWith: targetRecord.detail,
            date: target.date,
            reference
          },
          ...items.map((entry) =>
            entry.description === "Record" && entry.reference === packageReference
              ? { ...entry, remainingQuantity: Math.max(0, (entry.remainingQuantity ?? 0) - 1) }
              : entry
          )
        ]);
      }
    }
    if (target && status === "planned" && target.status === "cancelled" && targetRecord) {
      if (target.cancellationOutcome === "allowance") {
        adjustCancellationUsage(targetRecord.id, -1);
      }
      if (target.cancellationOutcome === "session") {
        setCustomers((items) =>
          items.map((customer) =>
            customer.id === target.customerId
              ? { ...customer, remainingSessions: customer.remainingSessions + 1 }
              : customer
          )
        );
        const reference = `C${target.id.replace(/\D/g, "")}`;
        const packageReference = `R${targetRecord.id.replace(/\D/g, "")}`;
        setLedger((items) =>
          items
            .filter((entry) => entry.reference !== reference)
            .map((entry) =>
              entry.description === "Record" && entry.reference === packageReference
                ? { ...entry, remainingQuantity: (entry.remainingQuantity ?? 0) + 1 }
                : entry
            )
        );
      }
    }
    setSelectedId(null);
    showToast(
      status === "completed"
        ? "Seans tamamlandı ve hak bakiyesi güncellendi."
        : status === "cancelled"
          ? cancellationOutcome === "allowance"
            ? "Randevu iptal edildi ve 1 iptal hakkı kullanıldı."
            : "İptal hakkı kalmadığı için 1 seans hakkı kapatıldı."
          : "Randevu yeniden planlandı."
    );
  }

  async function setStatus(
    id: string,
    status: AppointmentStatus,
    sessionNotes?: string,
    completedBy?: string,
    cancellationMode: CancellationMode = "customer",
    cancellationReason?: string,
    extendExpiryDays?: number
  ) {
    const target = appointments.find((appointment) => appointment.id === id);
    if (!target) return;
    const targetRecord = records.find((record) => record.id === target.recordId);
    const cancellationRemaining = targetRecord
      ? Math.max(
          0,
          (targetRecord.cancellationAllowance ?? 0) - (targetRecord.cancellationsUsed ?? 0)
        )
      : 0;

    try {
      await setAppointmentStatus(
        target,
        status,
        sessionNotes,
        completedBy,
        cancellationMode,
        cancellationReason,
        extendExpiryDays
      );
      setSelectedId(null);
      showToast(
        status === "completed"
          ? "Seans tamamlandı ve hak bakiyesi güncellendi."
          : status === "cancelled"
            ? cancellationMode === "studio"
              ? `Randevu iptal edildi; haklar korundu${extendExpiryDays === 7 ? " ve paket süresi 7 gün uzatıldı" : ""}.`
              : cancellationRemaining > 0
              ? "Randevu iptal edildi ve 1 iptal hakkı kullanıldı."
              : "İptal hakkı kalmadığı için 1 seans hakkı kapatıldı."
            : "Randevu yeniden planlandı."
      );
    } catch (statusError) {
      showToast(studioErrorCopy(statusError));
    }
  }

  function created(createdAppointments: Appointment[]) {
    if (!createdAppointments.length) {
      setNewAppointmentOpen(false);
      setNewAppointmentCustomerId(null);
      setNewAppointmentDate(null);
      showToast("Seans oluşturuldu ve program güncellendi.");
      return;
    }
    setAppointments((current) => [...current, ...createdAppointments].sort((a, b) => a.time.localeCompare(b.time)));
    setNewAppointmentOpen(false);
    setNewAppointmentCustomerId(null);
    setNewAppointmentDate(null);
    showToast(createdAppointments.length > 1 ? `${createdAppointments.length} haftalık randevu oluşturuldu.` : `Randevu oluşturuldu · ${createdAppointments[0].id}`);
  }

  async function deleteAppointment(id: string, removalNote?: string) {
    const target = appointments.find((appointment) => appointment.id === id);
    if (!target) return;
    try {
      await voidAppointment(target, removalNote);
      setSelectedId(null);
      showToast("Randevu programdan kaldırıldı; işlem geçmişi korundu.");
    } catch (deleteError) {
      showToast(studioErrorCopy(deleteError, "Seans kaydı kaldırılamadı. Güncel durumu kontrol edin."));
    }
  }

  let content: React.ReactNode;
  switch (page) {
    case "appointments":
      content = <AppointmentsPage appointments={appointments} onOpen={(item) => setSelectedId(item.id)} onNew={() => openNewAppointment()} onBlock={() => setNewHoldOpen(true)} />;
      break;
    case "customers":
      content = <FullCustomersPage onNewAppointment={(customerId) => openNewAppointment(customerId)} onToast={showToast} openCustomerId={customerFocusId} />;
      break;
    case "workshops":
      content = <WorkshopPage onToast={showToast} />;
      break;
    case "finance":
      content = financeUnlocked
        ? <FullFinancePage onToast={showToast} onOpenCustomer={openCustomerOverlay} />
        : <FinanceGate onUnlock={() => { setFinanceUnlocked(true); showToast("Kasa yetkisi açıldı."); }} />;
      break;
    case "opportunities":
      content = <FullOpportunitiesPage onToast={showToast} onOpenCustomer={openCustomerOverlay} />;
      break;
    case "messages":
      content = <FullMessagesPage onToast={showToast} />;
      break;
    case "team":
      content = <TeamPage onToast={showToast} />;
      break;
    case "notes":
      content = <NotesPage onToast={showToast} />;
      break;
    case "work":
      content = <GoalsTasksPage onToast={showToast} />;
      break;
    case "system":
      content = <SystemHealthBoundary><SystemHealthPage /></SystemHealthBoundary>;
      break;
    default:
      content = <Dashboard appointments={appointments} onOpenAppointment={(item) => setSelectedId(item.id)} onNewAppointment={() => openNewAppointment()} onNewHold={() => setNewHoldOpen(true)} onNavigate={setPage} onOpenCustomer={openCustomerOverlay} />;
  }

  if (connectionState === "loading") {
    return (
      <div className="sql-loading-screen">
        <span className="loading-logo"><img src={logoSrc} alt="The Heal Hub" /></span>
        <div className="loading-pulse" />
        <span className="eyebrow">The Heal Hub - Portal</span>
        <h1>Portal hazırlanıyor</h1>
        <p>Müşteriler, paketler ve yakın tarihli programınız güvenli biçimde hazırlanıyor.</p>
      </div>
    );
  }

  if (connectionState === "error" && customers.length === 0) {
    return (
      <div className="sql-loading-screen error">
        <XCircle size={34} />
        <span className="eyebrow">Bilgiler yenilenemedi</span>
        <h1>Portal şu anda açılamıyor</h1>
        <p>{connectionError ? "Sistem bağlantısını kontrol edip yeniden deneyin." : "Kısa bir süre sonra yeniden deneyin."}</p>
        <button className="button primary" onClick={() => refresh()}>Yeniden dene</button>
      </div>
    );
  }

  return (
    <div
      className={`app-shell ${workspaceSettings.compactView ? "compact-view" : ""} ${operationState?.phase === "working" ? "operation-busy" : ""}`}
      aria-busy={operationState?.phase === "working"}
    >
      <Sidebar page={page} onNavigate={setPage} open={menuOpen} onClose={() => setMenuOpen(false)} onSettings={() => { setSettingsOpen(true); setMenuOpen(false); }} onProfile={() => { setProfileOpen(true); setMenuOpen(false); }} />
      <div className="workspace">
        <Topbar
          onMenu={() => setMenuOpen(true)}
          onNewAppointment={() => openNewAppointment()}
          query={globalSearch}
          onQueryChange={setGlobalSearch}
          onOpenCustomer={openCustomer}
          onOpenAppointment={(id) => { setPage("appointments"); setSelectedId(id); setGlobalSearch(""); }}
          onNavigate={setPage}
        />
        <main>
          <PortalPageBoundary key={page} label={navItems.find((item) => item.id === page)?.label ?? "Portal"}>
            {content}
          </PortalPageBoundary>
        </main>
      </div>
      <MobileNav page={page} onNavigate={setPage} onMore={() => setMenuOpen(true)} />
      <AppointmentDrawer
        appointment={selectedAppointment}
        onClose={() => setSelectedId(null)}
        onStatus={setStatus}
        onDelete={deleteAppointment}
        onOpenCustomer={(customerId) => {
          setSelectedId(null);
          openCustomerOverlay(customerId);
        }}
        onNewAppointments={created}
        onHoldDone={showToast}
      />
      <NewAppointmentModal
        open={newAppointmentOpen}
        onClose={() => {
          setNewAppointmentOpen(false);
          setNewAppointmentCustomerId(null);
          setNewAppointmentDate(null);
        }}
        onCreated={created}
        initialCustomerId={newAppointmentCustomerId}
        initialDate={newAppointmentDate}
      />
      <NewHoldModal
        open={newHoldOpen}
        onClose={() => setNewHoldOpen(false)}
        onCreated={() => {
          setNewHoldOpen(false);
          showToast("Bloke oluşturuldu; paket veya seans hakkı kullanılmadı.");
        }}
        initialDate={newAppointmentDate}
      />
      {quickCustomerId && (
        <CustomerOverlay
          customerId={quickCustomerId}
          onClose={() => setQuickCustomerId(null)}
          onNewAppointment={(customerId) => {
            setQuickCustomerId(null);
            openNewAppointment(customerId);
          }}
          onToast={showToast}
        />
      )}
      {settingsOpen && <SettingsDrawer onClose={() => setSettingsOpen(false)} onSaved={() => showToast("Ayarlar kaydedildi.")} />}
      {profileOpen && <ProfileDrawer onClose={() => setProfileOpen(false)} onLockFinance={() => setFinanceUnlocked(false)} onToast={showToast} />}
      <OperationFeedback state={operationState} />
      {toast && <div className="toast"><CheckCircle2 size={18} />{toast}</div>}
    </div>
  );
}
